import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../../lib/api';
import { showToast } from '../../../components/Toast';
import { formatCurrency, formatNumber, formatQuantity } from '../../../utils/formatters';

// ── Tipi ────────────────────────────────────────────────────────────────────
export type TipoRigaPreventivo =
  | 'ARTICOLO'
  | 'ARTICOLO_CONFIGURATO'
  | 'DESCRITTIVA'
  | 'A_CORPO'
  | 'RICORRENTE';
export type ModalitaConfigurazione = 'DA_MODELLO' | 'DA_PRECONFIGURATO' | 'DIFFERITA_IN_ORDINE';

// ── Costanti ricorrenza (Nodo 1) ────────────────────────────────────────────
const TIPOLOGIE_RICORRENTI = ['LICENZA', 'CANONE', 'CONSUMI', 'COMMISSIONI'] as const;
type TipologiaRicorrente = typeof TIPOLOGIE_RICORRENTI[number];

const SOTTOTIPO_LABEL: Record<TipologiaRicorrente, string> = {
  LICENZA: 'Licenza',
  CANONE: 'Canone',
  CONSUMI: 'Consumi',
  COMMISSIONI: 'Commissioni',
};

const PERIODICITA_LABEL: Record<string, string> = {
  MENSILE: 'Mensile',
  TRIMESTRALE: 'Trimestrale',
  SEMESTRALE: 'Semestrale',
  ANNUALE: 'Annuale',
};

const MODALITA_PAGAMENTO_LABEL: Record<string, string> = {
  A_PREVENTIVO: 'A preventivo (importo fisso)',
  A_CONSUNTIVO_VOLUMI: 'A consuntivo - volumi (tariffa unitaria)',
  A_CONSUNTIVO_TRANSAZIONI: 'A consuntivo - transazioni (fissa + %)',
};
export type TipoRigaCapitolato =
  | 'BASE_CAPITOLATO'
  | 'EXTRA_CAPITOLATO_SOSTITUTIVO'
  | 'EXTRA_CAPITOLATO_AGGIUNTIVO'
  | 'NON_CAPITOLATO';
export type ApproccioCalcoloRiga = 'NESSUNO' | 'RICARICO_SU_COSTI' | 'SCONTO_DA_LISTINO';

export interface RigaLite {
  id: number;
  preventivoId: number;
  raggruppamentoId: number | null;
  ordine: number;
  tipoRiga: TipoRigaPreventivo;
  alternativaGruppo: string | null;
  alternativaScelta: boolean;
  etichettaAlternativa: string | null;
  articoloId: number | null;
  codicePrecopy: string | null;
  descrizione: string;
  descrizioneEstesa: string | null;
  umVendita: string | null;
  quantita: string | null;
  prezzoUnitario: string | null;
  prezzoCalcolato: string | null;
  sconto: string | null;
  codiceIvaId: number | null;
  modalitaConfigurazione: ModalitaConfigurazione | null;
  modelloConfigurazioneId?: number | null;
  articoloPrecConfiguratoId?: number | null;
  approccioCalcolo?: ApproccioCalcoloRiga | null;
  percentualeRicaricoRiga?: string | null;
  scontoSuCalcolato?: string | null;
  prezzoListino?: string | null;
  scontoAcquistoCodice?: string | null;
  scontoAcquistoPercentuale?: string | null;
  scontoVenditaCodice?: string | null;
  scontoVenditaPercentuale?: string | null;
  prezzoSovrascritto?: boolean;
  tipoRigaCapitolato: TipoRigaCapitolato;
  offertaCapRigaId?: number | null;
  offertaCapRigaAccessoriaId?: number | null;
  scorporoCapitolato: string | null;

  // ── Nodo 1: campi ricorrenza freeze (solo per tipoRiga = RICORRENTE) ──
  sottotipoRicorrenza?: 'LICENZA' | 'CANONE' | 'CONSUMI' | 'COMMISSIONI' | null;
  periodicita?: 'MENSILE' | 'TRIMESTRALE' | 'SEMESTRALE' | 'ANNUALE' | null;
  durataMesi?: number | null;
  dataInizio?: string | null;
  dataFine?: string | null;
  modalitaPagamento?: 'A_PREVENTIVO' | 'A_CONSUNTIVO_VOLUMI' | 'A_CONSUNTIVO_TRANSAZIONI' | null;
  tariffaUnitaria?: string | null;
  tariffaFissaTransazione?: string | null;
  percentualeTransazione?: string | null;
}

export interface RaggruppamentoLite {
  id: number;
  ordine: number;
  etichetta: string;
  livello?: 1 | 2 | 3;
  parentId?: number | null;
}

export interface PreventivoLite {
  id: number;
  societaId: number;
  offertaCapitolatoId: number | null;
  ricaricoImpresaFreeze: string | null;
  codiceIvaDefaultId: number;
  tipoFatturazioneFreeze: 'IMPRESA_PAGA_TUTTO' | 'CLIENTE_PAGA_DIRETTAMENTE' | null;
  acquirenteId: number | null;
  flussoCommerciale: 'VENDITA' | 'APPALTO_CONTRATTO' | null;
  offertaCapitolato: { id: number; codice: string; oggetto: string; ricarico: string | null } | null;
}

// T8: tipi riga incompatibili con il flusso VENDITA — nascosti nella modale.
// Allineato a backend/src/services/validazioneFlussoCommerciale.js.
const TIPI_INCOMPATIBILI_VENDITA = new Set<TipoRigaPreventivo>([
  'ARTICOLO_CONFIGURATO',
  'RICORRENTE',
]);

interface CodiceIvaOption {
  id: number;
  codice: string;
  descrizione: string;
  aliquota: string;
}

interface ModelloOption {
  id: number;
  codice: string;
  nome: string;
  articoloPadreCodice: string;
}

interface PreconfOption {
  id: number;
  codice: string;
  nome: string;
  articoloPadreId: number;
  articoloPadreCodice: string;
  articoloPadreNome: string;
  umVenditaSimbolo: string;
  nComponenti: number;
}

interface ArticoloOption {
  id: number;
  codice: string;
  descrizioneBase: string;
  tipologia: string;
  umVendita?: { simbolo: string } | null;
  umAcquisto?: { simbolo: string } | null;
  convertitore?: string;
  marca?: { id: number; nome: string } | null;
  categoriaMerceologica?: { id: number; nome: string } | null;
  collezione?: { id: number; nome: string } | null;
  formato?: { id: number; nome: string } | null;
  qtaMinOrdinabile?: string | null;
  fornitore?: { id: number; ragioneSociale?: string | null; nome?: string | null } | null;
  prezzoStandard?: string | null;
  prezzoListinoVendita?: string | null;

  // ── Nodo 1: default ricorrenza (popolati solo per tipologie ricorrenti) ──
  prodottoId?: number | null;
  periodicitaDefault?: string | null;
  durataMesiDefault?: number | null;
  modalitaPagamentoDefault?: string | null;
  tariffaUnitariaDefault?: string | number | null;
  tariffaFissaTransazioneDefault?: string | number | null;
  percentualeTransazioneDefault?: string | number | null;
}

interface ArticoloIntercambiabileInfo {
  isIntercambiabile: boolean;
  numeroAlternativi: number;
  prezzoMax: string | null;
  articoloMaxPrezzo: { id: number; codice: string; descrizioneBase: string } | null;
}

interface ScontoOption {
  id: number;
  codice: string;
  descrizione: string | null;
  percentualeEquivalente: number;
  attivo: boolean;
}

interface ScontoFornitoreLevels {
  vendBasso?: { id: number; codice: string; percentualeEquivalente: number } | null;
  vendMedio?: { id: number; codice: string; percentualeEquivalente: number } | null;
  vendAlto?:  { id: number; codice: string; percentualeEquivalente: number } | null;
  vendMax?:   { id: number; codice: string; percentualeEquivalente: number } | null;
  acqBasso?:  { id: number; codice: string; percentualeEquivalente: number } | null;
  acqMedio?:  { id: number; codice: string; percentualeEquivalente: number } | null;
  acqAlto?:   { id: number; codice: string; percentualeEquivalente: number } | null;
  acqMax?:    { id: number; codice: string; percentualeEquivalente: number } | null;
}

interface OffertaCapRigaOption {
  id: number;
  ordine: number;
  codicePrecopy: string;
  nomePrecopy: string;
  umVenditaPrecopy: string;
  prezzoUnitario: string;
  scorporo: string | null;
  descrizioneRiga: string | null;
  articoloPreconfigId?: number | null;
  isAccessoria?: boolean;
}

interface ComponenteRiga {
  id: number;
  rigaPreventivoId: number;
  ordine: number;
  tipologia: string;
  isPrincipale: boolean;
  isInDescrizione: boolean;
  articoloId: number | null;
  codicePrecopy: string | null;
  descrizione: string;
  umVendita: string | null;
  quantita: string | null;
  sfrido: string | null;
  prezzoListino: string | null;
  scontoAcquisto: string | null;
  scontoAcquistoCodice?: string | null;
  scontoVendita: string | null;
  scontoVenditaCodice?: string | null;
  costoUnitario: string | null;
  prezzoVenditaUnitario: string | null;
}

const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

// Icona "apri in una nuova scheda" — usata per i link di consultazione
// dalle celle della tabella componenti verso le pagine di archivio.
const ExternalLinkIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

interface SfridoRecord {
  id: number;
  quantitaDa: number;
  quantitaA: number | null;
  sfrido: number;
}

interface Props {
  preventivo: PreventivoLite;
  rigaEsistente: RigaLite | null;
  righeDelPreventivo: RigaLite[];
  raggruppamenti: RaggruppamentoLite[];
  codiciIva: CodiceIvaOption[];
  onClose: () => void;
  onSaved: () => void;
}

function toStr(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v);
}

function parseNumOrNull(s: string): number | null {
  if (s === '') return null;
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

// ── Sotto-componente: dropdown custom sconti (2 sezioni: fornitore + sistema) ──
function ScontoDropdown({
  open,
  onClose,
  onSeleziona,
  suggeriti,
  scontiCatalogo,
}: {
  open: boolean;
  onClose: () => void;
  onSeleziona: (codice: string) => void;
  suggeriti: { codice: string; percentualeEquivalente: number }[];
  scontiCatalogo: ScontoOption[];
}) {
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const catalogoOrd = [...scontiCatalogo].sort((a, b) => a.codice.localeCompare(b.codice));
  const hasFornitore = suggeriti.length > 0;
  const hasCatalogo = catalogoOrd.length > 0;

  return (
    <div className="bop-sconto-dropdown" ref={dropdownRef}>
      {hasFornitore && (
        <>
          <div className="bop-sconto-dropdown__section-title">Fornitore</div>
          {suggeriti.map((s, i) => (
            <button
              key={`forn-${i}`}
              type="button"
              className="bop-sconto-dropdown__item"
              onMouseDown={e => { e.preventDefault(); onSeleziona(s.codice); onClose(); }}
            >
              <span className="bop-sconto-dropdown__codice">{s.codice}</span>
              <span className="bop-sconto-dropdown__perc">
                {formatNumber(s.percentualeEquivalente)}%
              </span>
            </button>
          ))}
        </>
      )}
      {hasFornitore && hasCatalogo && <div className="bop-sconto-dropdown__separator" />}
      {hasCatalogo && (
        <>
          <div className="bop-sconto-dropdown__section-title">Sconti di sistema</div>
          {catalogoOrd.map(s => (
            <button
              key={s.id}
              type="button"
              className="bop-sconto-dropdown__item"
              onMouseDown={e => { e.preventDefault(); onSeleziona(s.codice); onClose(); }}
            >
              <span className="bop-sconto-dropdown__codice">{s.codice}</span>
              {s.descrizione && (
                <span className="bop-sconto-dropdown__desc">— {s.descrizione}</span>
              )}
              <span className="bop-sconto-dropdown__perc">
                {formatNumber(s.percentualeEquivalente)}%
              </span>
            </button>
          ))}
        </>
      )}
      {!hasFornitore && !hasCatalogo && (
        <div className="bop-sconto-dropdown__section-title">Nessuno sconto disponibile</div>
      )}
    </div>
  );
}

// ── Sotto-componente: input sconto (testo) con calcolo % e chip suggerimenti ──
function ScontoInput({
  codice,
  percentuale,
  onChange,
  scontiCatalogo,
  suggeriti,
  placeholder,
}: {
  codice: string;
  percentuale: string;
  onChange: (codice: string, percentuale: string) => void;
  scontiCatalogo: ScontoOption[];
  suggeriti: { codice: string; percentualeEquivalente: number }[];
  placeholder?: string;
}) {
  const [calcError, setCalcError] = useState<string | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const ricalcola = async (nuovoCodice: string) => {
    if (!nuovoCodice.trim()) {
      onChange('', '');
      setCalcError(null);
      return;
    }
    const match = scontiCatalogo.find(s => s.codice === nuovoCodice.trim());
    if (match) {
      onChange(match.codice, String(match.percentualeEquivalente));
      setCalcError(null);
      return;
    }
    setCalculating(true);
    try {
      // @api POST /api/sconti/calcola {"stringa":"string"} -> {"valida":"boolean","percentualeEquivalente":"number","moltiplicatore":"number","errore":"string?"}
      const res = await apiFetch<{ valida: boolean; percentualeEquivalente?: number; errore?: string }>(
        '/api/sconti/calcola',
        { method: 'POST', body: JSON.stringify({ stringa: nuovoCodice.trim() }) }
      );
      if (res.valida && res.percentualeEquivalente != null) {
        onChange(nuovoCodice.trim(), String(res.percentualeEquivalente));
        setCalcError(null);
      } else {
        onChange(nuovoCodice.trim(), '');
        setCalcError(res.errore || 'Stringa sconto non valida');
      }
    } catch {
      setCalcError('Errore calcolo sconto');
    } finally {
      setCalculating(false);
    }
  };

  return (
    <div className="prev-mod-sconto-input">
      <div className="prev-mod-sconto-input__row">
        <input
          className="bop-input"
          type="text"
          value={codice}
          onChange={e => ricalcola(e.target.value)}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setTimeout(() => setDropdownOpen(false), 200)}
          placeholder={placeholder || 'es. 10+5+2'}
        />
        <ScontoDropdown
          open={dropdownOpen}
          onClose={() => setDropdownOpen(false)}
          onSeleziona={codiceSel => ricalcola(codiceSel)}
          suggeriti={suggeriti}
          scontiCatalogo={scontiCatalogo}
        />
      </div>
      <div className="prev-mod-sconto-input__perc">
        <span className="prev-mod-sconto-input__perc-val">
          {calculating ? '…' : percentuale ? `${formatNumber(parseFloat(percentuale))}%` : '—'}
        </span>
        <Link
          to="/anagrafiche/articoli/sconti-fornitori"
          target="_blank"
          rel="noopener noreferrer"
          className="mcr-cell-link"
          title="Apri gli sconti fornitori in una nuova scheda"
        >
          <ExternalLinkIcon />
        </Link>
      </div>
      {calcError && <div className="bop-text-danger prev-mod-sconto-input__error">{calcError}</div>}
    </div>
  );
}

// ── Sotto-componente: autocomplete pre-configurati con debounce ─────────────
function AutocompletePreconfigurato({
  societaId,
  onSeleziona,
  onAnnullaSostituzione,
}: {
  societaId: number;
  onSeleziona: (p: PreconfOption) => void;
  onAnnullaSostituzione?: () => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PreconfOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams({ societaId: String(societaId) });
        if (query.trim()) qs.set('q', query.trim());
        // @api GET /api/articoli-preconfigurati?societaId={id}&q={q} -> {"success":true,"data":[{"id":"number","codice":"string","nome":"string","articoloPadreId":"number","articoloPadreCodice":"string","articoloPadreNome":"string","umVenditaSimbolo":"string","nComponenti":"number"}]}
        const r = await apiFetch<{ success: boolean; data: PreconfOption[] }>(
          `/api/articoli-preconfigurati?${qs}`,
        );
        setResults(r.data || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, societaId]);

  return (
    <div className="prev-mod-autocomplete">
      <div className="prev-mod-autocomplete__input-row">
        <input
          className="bop-input"
          placeholder="Cerca per codice o nome..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          autoFocus
        />
        {onAnnullaSostituzione && (
          <button
            type="button"
            className="bop-btn bop-btn-secondary"
            onClick={onAnnullaSostituzione}
          >
            Annulla cambio
          </button>
        )}
      </div>
      {open && (
        <div className="prev-mod-autocomplete__dropdown">
          {loading && <div className="prev-mod-autocomplete__empty">Ricerca in corso…</div>}
          {!loading && results.length === 0 && (
            <div className="prev-mod-autocomplete__empty">Nessun preconfigurato trovato</div>
          )}
          {!loading && results.map(p => (
            <button
              key={p.id}
              type="button"
              className="prev-mod-autocomplete__item"
              onMouseDown={e => { e.preventDefault(); onSeleziona(p); setQuery(''); setOpen(false); }}
            >
              <div className="prev-mod-autocomplete__line1">
                <span className="prev-mod-autocomplete__codice">{p.codice}</span>
                <span className="prev-mod-autocomplete__nome">{p.nome}</span>
              </div>
              <div className="prev-mod-autocomplete__line2">
                <span>Padre: <strong>{p.articoloPadreCodice}</strong> — {p.articoloPadreNome}</span>
                <span>UM {p.umVenditaSimbolo} · {p.nComponenti} componenti</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sotto-componente: popover cambia articolo per componente ────────────────
function CambiaArticoloComponentePopover({
  societaId,
  onClose,
  onSeleziona,
}: {
  compId: number;
  societaId: number;
  onClose: () => void;
  onSeleziona: (nuovoArt: ArticoloOption) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ArticoloOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const q = query.trim();
        const qs = `societaId=${societaId}&limit=20${q ? `&q=${encodeURIComponent(q)}` : ''}`;
        // @api GET /api/anagrafiche/articoli?societaId={id}&q={q}&limit=20 -> {"success":true,"data":[{"id":"number","codice":"string","descrizioneBase":"string","umVendita":{"simbolo":"string"},"prezzoListinoVendita":"string|null"}]}
        const r = await apiFetch<{ success: boolean; data: ArticoloOption[] }>(
          `/api/anagrafiche/articoli?${qs}`,
        );
        setResults(r.data || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, societaId]);

  return (
    <div className="prev-modal" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="prev-modal__box prev-mod-cambia-art-box">
        <div className="prev-modal__header">
          <h2 className="prev-modal__title">Cambia articolo componente</h2>
          <button className="prev-modal__close" onClick={onClose}>
            <XIcon />
          </button>
        </div>
        <div className="prev-modal__body">
          <div className="bop-form-group">
            <label className="bop-label">Cerca articolo</label>
            <input
              className="bop-input"
              placeholder="Codice o descrizione..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoFocus
            />
          </div>
          <div className="mcr-articolo-results">
            {loading && <div className="prev-mod-autocomplete__empty">Ricerca in corso…</div>}
            {!loading && results.length === 0 && (
              <div className="prev-mod-autocomplete__empty">Nessun articolo trovato</div>
            )}
            {!loading && results.map(a => (
              <button
                key={a.id}
                type="button"
                className="mcr-articolo-result"
                onMouseDown={e => { e.preventDefault(); onSeleziona(a); }}
              >
                <div className="mcr-articolo-result__line1">
                  <span className="mcr-articolo-result__codice">{a.codice}</span>
                  {a.tipologia && (
                    <span className="bop-badge bop-badge-neutral prev-mod-tipologia-badge">
                      {a.tipologia.replace(/_/g, ' ')}
                    </span>
                  )}
                  <span className="mcr-articolo-result__desc">{a.descrizioneBase}</span>
                </div>
                <div className="mcr-articolo-result__line2">
                  {a.umVendita?.simbolo && (
                    <span className="mcr-articolo-result__meta">u.m. {a.umVendita.simbolo}</span>
                  )}
                  {a.categoriaMerceologica?.nome && (
                    <span className="mcr-articolo-result__meta">{a.categoriaMerceologica.nome}</span>
                  )}
                  {a.prezzoListinoVendita && (
                    <span className="mcr-articolo-result__price">
                      € {formatCurrency(parseFloat(a.prezzoListinoVendita))}
                      {a.umVendita?.simbolo ? ` / ${a.umVendita.simbolo}` : ''}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
        <div className="prev-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
        </div>
      </div>
    </div>
  );
}

// ── Modale Sfrido Formato ─────────────────────────────────────────────────
function ModalSfridoFormato({
  societaId, formatoId, formatoNome, sfridoCorrente,
  onClose, onSeleziona,
}: {
  societaId: number;
  formatoId: number;
  formatoNome: string;
  sfridoCorrente: number | null;
  onClose: () => void;
  onSeleziona: (s: number) => void;
}) {
  const [sfridi, setSfridi] = useState<SfridoRecord[]>([]);
  const [loading, setLoading] = useState(true);
  // Un errore di caricamento (es. permesso) non va confuso con "nessuno sfrido"
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const params = new URLSearchParams({
          formatoId: String(formatoId),
          societaId: String(societaId),
        });
        // @api GET /api/articoli-preconfigurati/sfridi-formato?societaId&formatoId -> {"success":true,"data":[{"id":"number","quantitaDa":"number","quantitaA":"number|null","sfrido":"number"}]}
        const res = await apiFetch<{ success: boolean; data: SfridoRecord[] }>(
          `/api/articoli-preconfigurati/sfridi-formato?${params}`,
        );
        setSfridi(res.data || []);
      } catch (err: unknown) {
        setSfridi([]);
        setErrore(err instanceof Error && err.message ? err.message : 'Errore nel caricamento degli sfridi');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [societaId, formatoId]);

  return (
    <div className="prev-modal" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="prev-modal__box">
        <div className="prev-modal__header">
          <h2 className="prev-modal__title">Sfridi — Formato {formatoNome}</h2>
          <button className="prev-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="prev-modal__body">
          {loading ? (
            <div className="bop-text-muted">Caricamento...</div>
          ) : errore ? (
            <div className="inline-error">Impossibile leggere gli sfridi: {errore}</div>
          ) : sfridi.length === 0 ? (
            <div className="bop-empty-state">
              Nessuno sfrido configurato per il formato «{formatoNome}». Gli scaglioni si impostano in
              Archivi → Configurazioni Articoli → Sfridi, sul formato esatto dell'articolo (formati con nome
              simile, es. «120X120» e «120x120 CM», sono distinti). Intanto puoi scrivere la percentuale a mano.
            </div>
          ) : (
            <table className="bop-table">
              <thead>
                <tr>
                  <th>Da (qtà)</th>
                  <th>A (qtà)</th>
                  <th>Sfrido %</th>
                  <th className="bop-text-right">Azione</th>
                </tr>
              </thead>
              <tbody>
                {sfridi.map(s => (
                  <tr key={s.id}>
                    <td>{formatQuantity(s.quantitaDa)}</td>
                    <td>{s.quantitaA != null ? formatQuantity(s.quantitaA) : '∞'}</td>
                    <td>
                      {s.sfrido === sfridoCorrente
                        ? <span className="bop-badge bop-badge-info">{formatNumber(s.sfrido)}%</span>
                        : <span>{formatNumber(s.sfrido)}%</span>}
                    </td>
                    <td className="bop-text-right">
                      <button className="bop-btn bop-btn-primary" onClick={() => onSeleziona(s.sfrido)}>Seleziona</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="bop-text-muted mcr-sfrido-modal-hint">
            Puoi anche inserire manualmente la percentuale nel campo Sfrido %
          </p>
        </div>
        <div className="prev-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}

export default function ModalConfigurazioneRiga({
  preventivo,
  rigaEsistente,
  righeDelPreventivo,
  raggruppamenti,
  codiciIva,
  onClose,
  onSaved,
}: Props) {
  const preventivoId = preventivo.id;
  // "Fuori capitolato": preventivo su capitolato CLIENTE_PAGA_DIRETT ma senza acquirente.
  // In questo stato l'editor riga non deve permettere agganci al capitolato.
  const fuoriCapitolato =
    preventivo.tipoFatturazioneFreeze === 'CLIENTE_PAGA_DIRETTAMENTE' &&
    !preventivo.acquirenteId;
  const hasCapitolato = !!preventivo.offertaCapitolatoId && !fuoriCapitolato;
  const ricaricoImpresa = parseFloat(preventivo.ricaricoImpresaFreeze ?? preventivo.offertaCapitolato?.ricarico ?? '0');

  // ── Stato ────────────────────────────────────────────────────────────────
  const [tipoRiga, setTipoRiga] = useState<TipoRigaPreventivo>(
    rigaEsistente?.tipoRiga ?? 'ARTICOLO',
  );
  const [raggruppamentoId, setRaggruppamentoId] = useState<number | null>(
    rigaEsistente?.raggruppamentoId ?? (raggruppamenti[0]?.id ?? null),
  );

  const [modalitaConfig, setModalitaConfig] = useState<ModalitaConfigurazione | null>(
    rigaEsistente?.modalitaConfigurazione ?? null,
  );
  const [modelloConfigId, setModelloConfigId] = useState<number | null>(
    rigaEsistente?.modelloConfigurazioneId ?? null,
  );
  const [articoloPrecConfId, setArticoloPrecConfId] = useState<number | null>(
    rigaEsistente?.articoloPrecConfiguratoId ?? null,
  );

  const [tipoRigaCap, setTipoRigaCap] = useState<TipoRigaCapitolato>(() => {
    // Se la riga esiste già, usa il suo valore (modifica esistente)
    if (rigaEsistente?.tipoRigaCapitolato) {
      return rigaEsistente.tipoRigaCapitolato as TipoRigaCapitolato;
    }
    // Nuova riga: default in base al contesto preventivo
    return hasCapitolato ? 'EXTRA_CAPITOLATO_AGGIUNTIVO' : 'NON_CAPITOLATO';
  });
  const [offertaCapRigaId, setOffertaCapRigaId] = useState<number | null>(
    rigaEsistente?.offertaCapRigaId ?? null,
  );
  const [offertaCapRigaAccessoriaId, setOffertaCapRigaAccessoriaId] = useState<number | null>(
    rigaEsistente?.offertaCapRigaAccessoriaId ?? null,
  );
  const [scorporoCapitolato, setScorporoCapitolato] = useState<string>(
    toStr(rigaEsistente?.scorporoCapitolato),
  );

  const [articoloInterInfo, setArticoloInterInfo] = useState<ArticoloIntercambiabileInfo | null>(null);
  const [articoloId, setArticoloId] = useState<number | null>(rigaEsistente?.articoloId ?? null);
  const [articoloLabel, setArticoloLabel] = useState<string>(
    rigaEsistente?.articoloId
      ? `${rigaEsistente.codicePrecopy || ''} ${rigaEsistente.descrizione || ''}`.trim()
      : '',
  );
  const [articoloSearch, setArticoloSearch] = useState<string>('');
  const [articoloResults, setArticoloResults] = useState<ArticoloOption[]>([]);
  const [articoloSearchOpen, setArticoloSearchOpen] = useState(false);

  const [descrizione, setDescrizione] = useState<string>(rigaEsistente?.descrizione ?? '');
  const [descrizioneEstesa, setDescrizioneEstesa] = useState<string>(
    rigaEsistente?.descrizioneEstesa ?? '',
  );
  const [umVendita, setUmVendita] = useState<string>(rigaEsistente?.umVendita ?? '');
  const [quantita, setQuantita] = useState<string>(toStr(rigaEsistente?.quantita ?? '1'));
  const [prezzoUnitario, setPrezzoUnitario] = useState<string>(toStr(rigaEsistente?.prezzoUnitario));
  const [prezzoOverride, setPrezzoOverride] = useState<boolean>(
    rigaEsistente?.prezzoSovrascritto === true ||
      rigaEsistente?.approccioCalcolo === 'NESSUNO' ||
      false,
  );
  // Gestione sconti a livello di riga (persistiti)
  const [scontoAcquistoCodice, setScontoAcquistoCodice] = useState<string>(
    rigaEsistente?.scontoAcquistoCodice ?? '',
  );
  const [scontoAcquistoPerc, setScontoAcquistoPerc] = useState<string>(
    rigaEsistente?.scontoAcquistoPercentuale != null ? String(rigaEsistente.scontoAcquistoPercentuale) : '',
  );
  const [scontoVenditaCodice, setScontoVenditaCodice] = useState<string>(
    rigaEsistente?.scontoVenditaCodice ?? '',
  );
  const [scontoVenditaPerc, setScontoVenditaPerc] = useState<string>(
    rigaEsistente?.scontoVenditaPercentuale != null ? String(rigaEsistente.scontoVenditaPercentuale) : '',
  );
  // Prezzo listino della riga (in u.m. vendita)
  const [prezzoListino, setPrezzoListino] = useState<string>(
    rigaEsistente?.prezzoListino != null ? String(rigaEsistente.prezzoListino) : '',
  );

  // ── Nodo 1: stato flow RICORRENTE ────────────────────────────────────────
  const [periodicita, setPeriodicita] = useState<string>(
    rigaEsistente?.periodicita ?? '',
  );
  const [durataMesi, setDurataMesi] = useState<string>(
    rigaEsistente?.durataMesi != null ? String(rigaEsistente.durataMesi) : '',
  );
  const [modalitaPagamento, setModalitaPagamento] = useState<string>(
    rigaEsistente?.modalitaPagamento ?? '',
  );
  const [tariffaUnitaria, setTariffaUnitaria] = useState<string>(
    rigaEsistente?.tariffaUnitaria ?? '',
  );
  const [tariffaFissaTransazione, setTariffaFissaTransazione] = useState<string>(
    rigaEsistente?.tariffaFissaTransazione ?? '',
  );
  const [percentualeTransazione, setPercentualeTransazione] = useState<string>(
    rigaEsistente?.percentualeTransazione ?? '',
  );
  const [dataInizio, setDataInizio] = useState<string>(
    rigaEsistente?.dataInizio ? rigaEsistente.dataInizio.slice(0, 10) : '',
  );
  const [dataFine, setDataFine] = useState<string>(
    rigaEsistente?.dataFine ? rigaEsistente.dataFine.slice(0, 10) : '',
  );
  // Cataloghi sconti (caricati on-demand)
  const [scontiCatalogo, setScontiCatalogo] = useState<ScontoOption[]>([]);
  const [scontiFornitore, setScontiFornitore] = useState<ScontoFornitoreLevels | null>(null);
  // Articolo selezionato (caricato al mount o su select)
  const [articoloSelezionato, setArticoloSelezionato] = useState<ArticoloOption | null>(null);
  const [sconto, setSconto] = useState<string>(toStr(rigaEsistente?.sconto ?? '0'));
  const [codiceIvaId, setCodiceIvaId] = useState<number | null>(
    rigaEsistente?.codiceIvaId ?? preventivo.codiceIvaDefaultId ?? null,
  );

  const [approccioCalcolo, setApproccioCalcolo] = useState<ApproccioCalcoloRiga>(
    rigaEsistente?.approccioCalcolo ?? 'NESSUNO',
  );
  const [percentualeRicaricoRiga, setPercentualeRicaricoRiga] = useState<string>(
    toStr(rigaEsistente?.percentualeRicaricoRiga),
  );
  const [scontoSuCalcolato, setScontoSuCalcolato] = useState<string>(
    toStr(rigaEsistente?.scontoSuCalcolato),
  );
  const [componenti, setComponenti] = useState<ComponenteRiga[]>([]);
  // Patch live: modifiche ai componenti non ancora persistite (in digit).
  // Chiave: id componente. Valore: oggetto parziale con campi editati.
  // Al blur di un input, la modifica va anche al backend tramite aggiornaComponente(),
  // ma durante il digit la patchLive mantiene il valore mostrato per le formule.
  const [patchLive, setPatchLive] = useState<Record<number, Partial<ComponenteRiga>>>({});

  const [modelli, setModelli] = useState<ModelloOption[]>([]);
  const [righeCapitolato, setRigheCapitolato] = useState<OffertaCapRigaOption[]>([]);

  // Id riga "corrente": dopo creazione (POST) diventa disponibile e abilita componenti
  const [currentRigaId, setCurrentRigaId] = useState<number | null>(rigaEsistente?.id ?? null);
  const [saving, setSaving] = useState(false);

  // ── Alternativa: stato selezione (visibile in edit, se la riga è in raggruppamento)
  // altTargetId values:
  //   null = slega (riga indipendente)
  //   -2   = "resta nel cluster X" (no-op rispetto al cluster, può aggiornare etichetta)
  //   -1   = nuova alternativa (crea cluster con prima riga candidata)
  //   >0   = lega alla riga target
  const [altTargetId, setAltTargetId] = useState<number | null>(
    rigaEsistente?.alternativaGruppo ? -2 : null,
  );
  const [altEtichetta, setAltEtichetta] = useState<string>(rigaEsistente?.etichettaAlternativa || '');
  const [altGruppoCorrente] = useState<string | null>(rigaEsistente?.alternativaGruppo ?? null);

  // ── Stato caso DA_PRECONFIGURATO ─────────────────────────────────────────
  const [preconfSelezionato, setPreconfSelezionato] = useState<PreconfOption | null>(null);
  // true solo se la riga è stata creata via crea-da-preconfigurato in questa sessione
  // (non caricata da rigaEsistente). Discrimina la chiusura con/senza DELETE.
  const [rigaAppenaCreata, setRigaAppenaCreata] = useState(false);
  const [sostituendoPreconf, setSostituendoPreconf] = useState(false);

  // Cache dettagli articolo per componente (per ricavare fornitoreId e tipologia)
  const [articoliDettaglioCache, setArticoliDettaglioCache] = useState<Record<number, ArticoloOption>>({});
  // Cache livelli sconto per fornitore (keyed by fornitoreId)
  const [scontiPerFornitore, setScontiPerFornitore] = useState<Record<number, ScontoFornitoreLevels>>({});
  // Info intercambiabili per componente (keyed by componenteId)
  const [componentiInterInfo, setComponentiInterInfo] = useState<Record<number, ArticoloIntercambiabileInfo>>({});
  // Popover "Cambia articolo" per un componente specifico
  const [cambiaArticoloCompId, setCambiaArticoloCompId] = useState<number | null>(null);
  // Modale "Scegli sfrido" per un componente specifico
  const [sfridoModalCompId, setSfridoModalCompId] = useState<number | null>(null);
  // Componente su cui è in corso il ricalcolo del prezzo di listino
  const [ricalcoloCompId, setRicalcoloCompId] = useState<number | null>(null);

  const isNew = !rigaEsistente;
  const isConfigurato = tipoRiga === 'ARTICOLO_CONFIGURATO';
  const isDescrittiva = tipoRiga === 'DESCRITTIVA';
  const isBaseCapitolato = tipoRigaCap === 'BASE_CAPITOLATO';

  // T8: card tipo riga nascoste in base al flusso commerciale del preventivo.
  // - VENDITA → nasconde ARTICOLO_CONFIGURATO e RICORRENTE.
  // - flussoCommerciale null/undefined (preventivi storici) → safe default, no filtro.
  // - In modifica di una riga esistente di tipo incompatibile, la card resta
  //   visibile (disabled) per non rompere la UI.
  const isTipoRigaNascosto = (t: TipoRigaPreventivo): boolean => {
    if (!preventivo.flussoCommerciale) return false;
    if (preventivo.flussoCommerciale !== 'VENDITA') return false;
    if (!TIPI_INCOMPATIBILI_VENDITA.has(t)) return false;
    if (tipoRiga === t) return false; // mai nascondere il tipo attualmente selezionato
    return true;
  };

  // ── Caricamento anagrafiche ──────────────────────────────────────────────
  useEffect(() => {
    if (!isConfigurato) return;
    (async () => {
      try {
        // @api GET /api/modelli-configurazione?societaId={id} -> {"success":true,"data":[{"id":"number","codice":"string","nome":"string","articoloPadreCodice":"string"}]}
        const r = await apiFetch<{ success: boolean; data: ModelloOption[] }>(
          `/api/modelli-configurazione?societaId=${preventivo.societaId}`,
        );
        setModelli(r.data || []);
      } catch {
        setModelli([]);
      }
    })();
  }, [isConfigurato, preventivo.societaId]);

  useEffect(() => {
    if (!hasCapitolato || !preventivo.offertaCapitolatoId) return;
    (async () => {
      try {
        // @api GET /api/offerte-capitolato/:id -> {"success":true,"data":{"righe":[{"id":"number","ordine":"number","codicePrecopy":"string","nomePrecopy":"string","umVenditaPrecopy":"string","prezzoUnitario":"string","scorporo":"string|null","descrizioneRiga":"string|null"}],"righeAccessorie":[{"id":"number","ordine":"number","codicePrecopy":"string","nomePrecopy":"string","umVenditaPrecopy":"string","prezzoUnitario":"string","scorporo":"string|null","descrizioneRiga":"string|null"}]}}
        const r = await apiFetch<{ success: boolean; data: { righe: OffertaCapRigaOption[]; righeAccessorie: OffertaCapRigaOption[] } }>(
          `/api/offerte-capitolato/${preventivo.offertaCapitolatoId}`,
        );
        const righe = (r.data?.righe || []).map(x => ({ ...x, isAccessoria: false }));
        const righeAcc = (r.data?.righeAccessorie || []).map(x => ({ ...x, isAccessoria: true }));
        setRigheCapitolato([...righe, ...righeAcc]);
      } catch {
        setRigheCapitolato([]);
      }
    })();
  }, [hasCapitolato, preventivo.offertaCapitolatoId]);

  // Carica catalogo sconti della società (per combo)
  useEffect(() => {
    if (!preventivo?.societaId) return;
    // @api GET /api/sconti?societaId={id} -> [{"id":"number","codice":"string","descrizione":"string|null","percentualeEquivalente":"number","attivo":"boolean"}]
    apiFetch<ScontoOption[]>(`/api/sconti?societaId=${preventivo.societaId}`)
      .then(data => setScontiCatalogo(Array.isArray(data) ? data.filter(s => s.attivo) : []))
      .catch(() => setScontiCatalogo([]));
  }, [preventivo?.societaId]);

  // Carica sconti del fornitore quando l'articolo ha un fornitore valorizzato
  useEffect(() => {
    if (!articoloSelezionato?.fornitore?.id || !preventivo?.societaId) {
      setScontiFornitore(null);
      return;
    }
    const fornitoreId = articoloSelezionato.fornitore.id;
    // @api GET /api/anagrafiche/sconti-fornitore/:fornitoreId?societaId={id} -> {"success":true,"data":"ScontoFornitoreLevels|null"}
    apiFetch<{ success: boolean; data: ScontoFornitoreLevels | null }>(
      `/api/anagrafiche/sconti-fornitore/${fornitoreId}?societaId=${preventivo.societaId}`,
    )
      .then(r => setScontiFornitore(r.data))
      .catch(() => setScontiFornitore(null));
  }, [articoloSelezionato?.fornitore?.id, preventivo?.societaId]);

  // In modifica: carica dettaglio articolo per popolare articoloSelezionato
  useEffect(() => {
    if (!articoloId || articoloSelezionato?.id === articoloId) return;
    // @api GET /api/anagrafiche/articoli/:id -> {"success":true,"data":"Articolo"}
    apiFetch<{ success: boolean; data: ArticoloOption }>(`/api/anagrafiche/articoli/${articoloId}`)
      .then(r => { if (r?.data) setArticoloSelezionato(r.data); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articoloId]);

  // Ricerca articoli con debounce.
  // Quando tipoRiga=ARTICOLO_CONFIGURATO e modalitaConfig=DIFFERITA_IN_ORDINE,
  // filtra solo gli articoli di tipologia PRODOTTO_CONFIGURATO.
  useEffect(() => {
    if (!articoloSearchOpen) return;
    const t = setTimeout(async () => {
      try {
        const q = articoloSearch.trim();
        const params = new URLSearchParams({
          societaId: String(preventivo.societaId),
          limit: '20',
        });
        if (q) params.set('q', q);

        // Filtro tipologia server-side in base al radio scelto.
        // Pattern allineato a quello che usa il filtro client-side
        // (articoliFiltratiPerRadio): ora il backend gia' filtra,
        // cosi' il limit=20 e' applicato sul sottoinsieme corretto.
        if (tipoRiga === 'ARTICOLO_CONFIGURATO') {
          // Mantieni il filtro singolo PRODOTTO_CONFIGURATO (anche per modalita' non DIFFERITA)
          params.set('tipologia', 'PRODOTTO_CONFIGURATO');
        } else if (tipoRiga === 'A_CORPO') {
          params.set('tipologia', 'RAGGRUPPAMENTO_PREZZO_CORPO');
        } else if (tipoRiga === 'RICORRENTE') {
          params.set('tipologie', 'LICENZA,CANONE,CONSUMI,COMMISSIONI');
        }
        // Per tipoRiga === 'ARTICOLO' nessun filtro server-side: il filtro
        // client-side esclude tipologie ricorrenti e raggruppamento; lasciare
        // che il server torni tutto va bene perche' statisticamente le tipologie
        // "ARTICOLO" sono la maggioranza in anagrafica.
        // Per tipoRiga === 'DESCRITTIVA' la combo articoli non viene
        // usata; la query non si attiva.
        // @api GET /api/anagrafiche/articoli?societaId={id}&q={q}&limit=20&tipologia={tip}&tipologie={csv} -> {"success":true,"data":[{"id":"number","codice":"string","descrizioneBase":"string","umVendita":{"simbolo":"string"},"marca":{"id":"number","nome":"string"},"categoriaMerceologica":{"id":"number","nome":"string"},"prezzoStandard":"string|null"}]}
        const r = await apiFetch<{ success: boolean; data: ArticoloOption[] }>(
          `/api/anagrafiche/articoli?${params.toString()}`,
        );
        setArticoloResults(r.data || []);
      } catch {
        setArticoloResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [articoloSearch, articoloSearchOpen, preventivo.societaId, isConfigurato, modalitaConfig, tipoRiga]);

  // Quando cambia modalità configurazione o tipo riga, resetta i risultati articolo per evitare
  // che rimangano visibili risultati filtrati con tipologia diversa.
  useEffect(() => {
    setArticoloResults([]);
  }, [modalitaConfig, tipoRiga]);

  // ── Nodo 1: reset campi non applicabili al cambio di tipoRiga ─────────────
  useEffect(() => {
    if (tipoRiga !== 'RICORRENTE') {
      setPeriodicita('');
      setDurataMesi('');
      setModalitaPagamento('');
      setTariffaUnitaria('');
      setTariffaFissaTransazione('');
      setPercentualeTransazione('');
      setDataInizio('');
      setDataFine('');
    }
    // Al cambio radio l'articoloId va resettato (il filtro combo cambia)
    if (isNew) {
      setArticoloId(null);
      setArticoloLabel('');
      setArticoloSelezionato(null);
      setArticoloInterInfo(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoRiga]);

  // ── Nodo 1: pre-popolazione campi RICORRENTE da default articolo ─────────
  useEffect(() => {
    if (tipoRiga !== 'RICORRENTE' || !articoloSelezionato) return;
    const a = articoloSelezionato;
    if (!periodicita && a.periodicitaDefault) {
      setPeriodicita(a.periodicitaDefault);
    }
    if (!durataMesi && a.durataMesiDefault != null) {
      setDurataMesi(String(a.durataMesiDefault));
    }
    if (!modalitaPagamento && a.modalitaPagamentoDefault) {
      setModalitaPagamento(a.modalitaPagamentoDefault);
    }
    if (!tariffaUnitaria && a.tariffaUnitariaDefault != null) {
      setTariffaUnitaria(String(a.tariffaUnitariaDefault));
    }
    if (!tariffaFissaTransazione && a.tariffaFissaTransazioneDefault != null) {
      setTariffaFissaTransazione(String(a.tariffaFissaTransazioneDefault));
    }
    if (!percentualeTransazione && a.percentualeTransazioneDefault != null) {
      setPercentualeTransazione(String(a.percentualeTransazioneDefault));
    }
    if (!dataInizio) {
      setDataInizio(new Date().toISOString().slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoRiga, articoloSelezionato]);

  // ── Nodo 1: auto-calcolo dataFine da dataInizio + durataMesi ─────────────
  useEffect(() => {
    if (!dataInizio || !durataMesi || Number(durataMesi) <= 0) return;
    const di = new Date(dataInizio);
    if (isNaN(di.getTime())) return;
    const df = new Date(di);
    df.setMonth(df.getMonth() + Number(durataMesi));
    setDataFine(df.toISOString().slice(0, 10));
  }, [dataInizio, durataMesi]);

  // Carica componenti quando currentRigaId cambia
  const caricaComponenti = useCallback(async () => {
    if (!currentRigaId) {
      setComponenti([]);
      return;
    }
    try {
      // @api GET /api/preventivi/:id/righe/:rigaId/componenti -> {"success":true,"data":[{"id":"number",...}]}
      const r = await apiFetch<{ success: boolean; data: ComponenteRiga[] }>(
        `/api/preventivi/${preventivoId}/righe/${currentRigaId}/componenti`,
      );
      setComponenti(r.data || []);
    } catch {
      setComponenti([]);
    }
  }, [preventivoId, currentRigaId]);

  useEffect(() => {
    caricaComponenti();
  }, [caricaComponenti]);

  // Quando i componenti arrivano da un refetch, pulisci patchLive
  useEffect(() => {
    setPatchLive({});
  }, [componenti.length]);

  // In modifica: carica dettagli preconfigurato se la riga esistente ne ha uno
  useEffect(() => {
    if (!articoloPrecConfId) return;
    if (preconfSelezionato && preconfSelezionato.id === articoloPrecConfId) return;
    (async () => {
      try {
        // @api GET /api/articoli-preconfigurati/:id -> {"success":true,"data":{"id":"number","codice":"string","nome":"string","articoloPadreId":"number","articoloPadreCodice":"string","articoloPadreNome":"string","umVenditaSimbolo":"string","componenti":"array"}}
        const r = await apiFetch<{ success: boolean; data: { id: number; codice: string; nome: string; articoloPadreId: number; articoloPadreCodice: string; articoloPadreNome: string; umVenditaSimbolo: string; componenti: { id: number }[] } }>(
          `/api/articoli-preconfigurati/${articoloPrecConfId}`,
        );
        if (r.data) {
          setPreconfSelezionato({
            id: r.data.id,
            codice: r.data.codice,
            nome: r.data.nome,
            articoloPadreId: r.data.articoloPadreId,
            articoloPadreCodice: r.data.articoloPadreCodice,
            articoloPadreNome: r.data.articoloPadreNome,
            umVenditaSimbolo: r.data.umVenditaSimbolo,
            nComponenti: (r.data.componenti || []).length,
          });
        }
      } catch { /* ignora */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articoloPrecConfId]);

  // Cache dettagli articolo per i componenti (fornitoreId, tipologia, prezzo listino max se intercambiabile)
  useEffect(() => {
    if (!componenti.length) return;
    const idsArticoli = Array.from(new Set(
      componenti.map(c => c.articoloId).filter((id): id is number => id != null)
    ));
    const daCaricare = idsArticoli.filter(id => !(id in articoliDettaglioCache));
    if (daCaricare.length === 0) return;
    (async () => {
      const nuovi: Record<number, ArticoloOption> = {};
      await Promise.all(daCaricare.map(async (id) => {
        try {
          // @api GET /api/anagrafiche/articoli/:id -> {"success":true,"data":"Articolo"}
          const r = await apiFetch<{ success: boolean; data: ArticoloOption }>(
            `/api/anagrafiche/articoli/${id}`,
          );
          if (r?.data) nuovi[id] = r.data;
        } catch { /* ignora */ }
      }));
      if (Object.keys(nuovi).length > 0) {
        setArticoliDettaglioCache(prev => ({ ...prev, ...nuovi }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componenti]);

  // Cache livelli sconti fornitore per i componenti
  useEffect(() => {
    if (!componenti.length || !preventivo?.societaId) return;
    const fornitoriUnici = Array.from(new Set(
      componenti
        .map(c => c.articoloId != null ? articoliDettaglioCache[c.articoloId]?.fornitore?.id : null)
        .filter((fid): fid is number => fid != null)
    ));
    const daCaricare = fornitoriUnici.filter(fid => !(fid in scontiPerFornitore));
    if (daCaricare.length === 0) return;
    (async () => {
      const nuovi: Record<number, ScontoFornitoreLevels> = {};
      await Promise.all(daCaricare.map(async (fid) => {
        try {
          // @api GET /api/anagrafiche/sconti-fornitore/:fornitoreId?societaId={id} -> {"success":true,"data":"ScontoFornitoreLevels|null"}
          const r = await apiFetch<{ success: boolean; data: ScontoFornitoreLevels | null }>(
            `/api/anagrafiche/sconti-fornitore/${fid}?societaId=${preventivo.societaId}`,
          );
          if (r?.data) nuovi[fid] = r.data;
        } catch { /* ignora */ }
      }));
      if (Object.keys(nuovi).length > 0) {
        setScontiPerFornitore(prev => ({ ...prev, ...nuovi }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componenti, articoliDettaglioCache, preventivo?.societaId]);

  // ── BASE_CAPITOLATO: crea riga da preconfigurato del capitolato ──────────
  const onSelezionaRigaCapBase = async (rigaCapId: number) => {
    const rigaCap = righeCapitolato.find(r => r.id === rigaCapId);
    if (!rigaCap) return;
    if (!rigaCap.articoloPreconfigId) {
      showToast('La riga capitolato non ha un articolo pre-configurato associato', 'error');
      return;
    }

    // Se esiste già una riga "fantasma" da una selezione precedente, eliminala
    if (currentRigaId && rigaAppenaCreata) {
      try {
        // @api DELETE /api/preventivi/:id/righe/:rigaId -> {"success":true}
        await apiFetch(`/api/preventivi/${preventivoId}/righe/${currentRigaId}`, { method: 'DELETE' });
        onSaved();
      } catch { /* silenzioso */ }
      setCurrentRigaId(null);
      setPreconfSelezionato(null);
      setComponenti([]);
      setComponentiInterInfo({});
      setRigaAppenaCreata(false);
    }

    // Carica i dati del preconfigurato
    let preconf: PreconfOption;
    try {
      // @api GET /api/articoli-preconfigurati/:id -> {"success":true,"data":{"id":"number","codice":"string","nome":"string","articoloPadreId":"number","articoloPadreCodice":"string","articoloPadreNome":"string","umVenditaSimbolo":"string"}}
      const r = await apiFetch<{ success: boolean; data: PreconfOption }>(
        `/api/articoli-preconfigurati/${rigaCap.articoloPreconfigId}`,
      );
      preconf = r.data;
    } catch (e) {
      showToast('Errore nel caricamento del pre-configurato', 'error');
      return;
    }

    // Crea la riga dal preconfigurato
    let rigaId: number | null = null;
    try {
      // @api POST /api/preventivi/:id/righe/crea-da-preconfigurato {"articoloPrecConfiguratoId":"number","raggruppamentoId":"number|null","codiceIvaId":"number|null"} -> {"success":true,"data":{"id":"number","nComponentiInseriti":"number"}}
      const r = await apiFetch<{ success: boolean; data: { id: number; nComponentiInseriti: number } }>(
        `/api/preventivi/${preventivoId}/righe/crea-da-preconfigurato`,
        {
          method: 'POST',
          body: JSON.stringify({ articoloPrecConfiguratoId: preconf.id, raggruppamentoId, codiceIvaId }),
        },
      );
      rigaId = r.data.id;
      showToast(`Riga creata da "${preconf.codice}" con ${r.data.nComponentiInseriti} componenti`, 'success');
    } catch (e) {
      showToast((e as Error).message || 'Errore nella creazione della riga', 'error');
      return;
    }

    // Aggiorna i campi capitolato sulla riga appena creata
    const val = rigaCap.scorporo != null ? rigaCap.scorporo : rigaCap.prezzoUnitario;
    try {
      // @api PUT /api/preventivi/:id/righe/:rigaId {...} -> {"success":true}
      await apiFetch(`/api/preventivi/${preventivoId}/righe/${rigaId}`, {
        method: 'PUT',
        body: JSON.stringify({
          tipoRiga: 'ARTICOLO_CONFIGURATO',
          raggruppamentoId,
          descrizione: preconf.nome,
          umVendita: preconf.umVenditaSimbolo,
          articoloId: preconf.articoloPadreId,
          quantita: 1,
          sconto: 0,
          codiceIvaId,
          modalitaConfigurazione: 'DA_PRECONFIGURATO',
          articoloPrecConfiguratoId: preconf.id,
          approccioCalcolo: 'NESSUNO',
          prezzoSovrascritto: true,
          prezzoUnitario: parseNumOrNull(toStr(val)),
          tipoRigaCapitolato: 'BASE_CAPITOLATO',
          offertaCapRigaId: rigaCapId,
          scorporoCapitolato: parseNumOrNull(toStr(val)),
        }),
      });
    } catch (e) {
      showToast('Errore nell\'aggiornamento del link capitolato', 'error');
    }

    // Aggiorna stato locale
    setCurrentRigaId(rigaId);
    setRigaAppenaCreata(true);
    setPreconfSelezionato(preconf);
    setArticoloPrecConfId(preconf.id);
    setDescrizione(preconf.nome);
    setUmVendita(preconf.umVenditaSimbolo);
    setArticoloId(preconf.articoloPadreId);
    setArticoloLabel(`${preconf.articoloPadreCodice} — ${preconf.articoloPadreNome}`);
    setTipoRiga('ARTICOLO_CONFIGURATO');
    setModalitaConfig('DA_PRECONFIGURATO');
    setScorporoCapitolato(toStr(val));
    setPrezzoUnitario(toStr(val));
    onSaved();
  };

  // ── Scelta riga capitolato: precompila scorporo e forza regole ──────────
  const onSelezionaRigaCapKey = async (key: string) => {
    if (!key) {
      setOffertaCapRigaId(null);
      setOffertaCapRigaAccessoriaId(null);
      setScorporoCapitolato('');
      return;
    }
    const isAcc = key.startsWith('acc-');
    const rigaId = parseInt(key.replace(/^(riga|acc)-/, ''), 10);
    setOffertaCapRigaId(isAcc ? null : rigaId);
    setOffertaCapRigaAccessoriaId(isAcc ? rigaId : null);
    const r = righeCapitolato.find(x => x.id === rigaId && !!x.isAccessoria === isAcc);
    if (r) {
      const val = r.scorporo != null ? r.scorporo : r.prezzoUnitario;
      setScorporoCapitolato(toStr(val));
      if (tipoRigaCap === 'BASE_CAPITOLATO') {
        setPrezzoUnitario(toStr(val));
        if (!isAcc && (!currentRigaId || rigaAppenaCreata)) {
          await onSelezionaRigaCapBase(rigaId);
        }
      }
    }
  };

  const onCambiaTipoCap = async (newTipo: TipoRigaCapitolato) => {
    // Se si abbandona BASE_CAPITOLATO con una riga fantasma, eliminala
    if (tipoRigaCap === 'BASE_CAPITOLATO' && newTipo !== 'BASE_CAPITOLATO' && currentRigaId && rigaAppenaCreata) {
      try {
        // @api DELETE /api/preventivi/:id/righe/:rigaId -> {"success":true}
        await apiFetch(`/api/preventivi/${preventivoId}/righe/${currentRigaId}`, { method: 'DELETE' });
        onSaved();
      } catch { /* silenzioso */ }
      setCurrentRigaId(null);
      setPreconfSelezionato(null);
      setComponenti([]);
      setComponentiInterInfo({});
      setRigaAppenaCreata(false);
      setOffertaCapRigaId(null);
      setOffertaCapRigaAccessoriaId(null);
      setScorporoCapitolato('');
      setTipoRiga('ARTICOLO_CONFIGURATO');
      setModalitaConfig(null);
    }

    setTipoRigaCap(newTipo);
    if (newTipo === 'EXTRA_CAPITOLATO_AGGIUNTIVO') {
      setOffertaCapRigaId(null);
      setOffertaCapRigaAccessoriaId(null);
      setScorporoCapitolato('0');
    } else if (newTipo === 'BASE_CAPITOLATO') {
      // Forza tipo riga e modalità per BASE_CAPITOLATO
      setTipoRiga('ARTICOLO_CONFIGURATO');
      setModalitaConfig('DA_PRECONFIGURATO');
      const refId = offertaCapRigaId ?? offertaCapRigaAccessoriaId;
      const isAcc = !offertaCapRigaId && !!offertaCapRigaAccessoriaId;
      if (refId) {
        const r = righeCapitolato.find(x => x.id === refId && !!x.isAccessoria === isAcc);
        if (r) {
          const val = r.scorporo != null ? r.scorporo : r.prezzoUnitario;
          setScorporoCapitolato(toStr(val));
          setPrezzoUnitario(toStr(val));
        }
      }
    }
  };

  // ── Scelta articolo: precompila campi ─────────────────────────────────────
  const fetchIntercambiabiliInfo = useCallback(async (artId: number, tipologia: string) => {
    if (tipologia !== 'INTERCAMBIABILE') {
      setArticoloInterInfo(null);
      return null;
    }
    try {
      // @api GET /api/anagrafiche/articoli/:id/intercambiabili-con-prezzo-max -> {"success":true,"data":{"isIntercambiabile":"boolean","numeroAlternativi":"number","prezzoMax":"string|null","articoloMaxPrezzo":"{id,codice,descrizioneBase}|null"}}
      const r = await apiFetch<{ success: boolean; data: ArticoloIntercambiabileInfo }>(
        `/api/anagrafiche/articoli/${artId}/intercambiabili-con-prezzo-max`,
      );
      setArticoloInterInfo(r.data);
      return r.data;
    } catch {
      setArticoloInterInfo(null);
      return null;
    }
  }, []);

  const onSelezionaArticolo = async (a: ArticoloOption) => {
    setArticoloId(a.id);
    setArticoloLabel(`${a.codice} — ${a.descrizioneBase}`);
    setArticoloSelezionato(a);
    if (!descrizione) setDescrizione(a.descrizioneBase);
    // UM SEMPRE dall'anagrafica articolo (readonly)
    setUmVendita(a.umVendita?.simbolo || '');
    setArticoloSearchOpen(false);
    setArticoloSearch('');

    if (!isConfigurato) {
      let listinoDaUsare = a.prezzoListinoVendita != null ? String(a.prezzoListinoVendita) : '';
      if (a.tipologia === 'INTERCAMBIABILE') {
        const info = await fetchIntercambiabiliInfo(a.id, a.tipologia);
        if (info?.prezzoMax != null) {
          listinoDaUsare = info.prezzoMax;
        }
      } else {
        setArticoloInterInfo(null);
      }
      setPrezzoListino(listinoDaUsare);
      if (approccioCalcolo === 'NESSUNO') {
        setApproccioCalcolo('SCONTO_DA_LISTINO');
      }
      setPrezzoOverride(false);
    }
  };

  const handleAggiornaArticolo = async () => {
    if (!articoloId) return;
    try {
      // @api GET /api/anagrafiche/articoli/:id -> {"success":true,"data":"Articolo"}
      const r = await apiFetch<{ success: boolean; data: ArticoloOption }>(
        `/api/anagrafiche/articoli/${articoloId}`,
      );
      const a = r.data;
      if (!a) return;

      setArticoloSelezionato(a);
      setUmVendita(a.umVendita?.simbolo || '');

      if (!isConfigurato) {
        let listinoDaUsare = a.prezzoListinoVendita != null ? String(a.prezzoListinoVendita) : '';
        if (a.tipologia === 'INTERCAMBIABILE') {
          const info = await fetchIntercambiabiliInfo(a.id, a.tipologia);
          if (info?.prezzoMax != null) {
            listinoDaUsare = info.prezzoMax;
          }
        } else {
          setArticoloInterInfo(null);
        }
        setPrezzoListino(listinoDaUsare);
      }
      showToast('Dati articolo aggiornati', 'success');
    } catch (e) {
      showToast((e as Error).message || 'Errore nel rileggere l\u0027articolo', 'error');
    }
  };

  // ── Nodo 1: filtro client-side combo articoli per tipo riga selezionato ──
  const articoliFiltratiPerRadio = useMemo<ArticoloOption[]>(() => {
    if (tipoRiga === 'DESCRITTIVA') return [];
    return articoloResults.filter(a => {
      switch (tipoRiga) {
        case 'ARTICOLO':
          return !['RAGGRUPPAMENTO_PREZZO_CORPO', 'LICENZA', 'CANONE', 'CONSUMI', 'COMMISSIONI'].includes(a.tipologia);
        case 'ARTICOLO_CONFIGURATO':
          return a.tipologia === 'PRODOTTO_CONFIGURATO';
        case 'A_CORPO':
          return a.tipologia === 'RAGGRUPPAMENTO_PREZZO_CORPO';
        case 'RICORRENTE':
          return (TIPOLOGIE_RICORRENTI as readonly string[]).includes(a.tipologia);
        default:
          return false;
      }
    });
  }, [tipoRiga, articoloResults]);

  // ── Calcolo live prezzo riga ARTICOLO (replica logica backend) ───────────
  const prezzoCalcolatoLive = useMemo<number | null>(() => {
    if (tipoRiga !== 'ARTICOLO') return null;
    const pl = parseFloat(prezzoListino || '0');
    if (!pl || pl <= 0) return null;

    if (approccioCalcolo === 'SCONTO_DA_LISTINO') {
      const sv = parseFloat(scontoVenditaPerc || '0');
      return parseFloat((pl * (1 - sv / 100)).toFixed(4));
    }
    if (approccioCalcolo === 'RICARICO_SU_COSTI') {
      const sa  = parseFloat(scontoAcquistoPerc || '0');
      const ric = parseFloat(percentualeRicaricoRiga || '0');
      const costo = pl * (1 - sa / 100);
      return parseFloat((costo * (1 + ric / 100)).toFixed(4));
    }
    return null;
  }, [tipoRiga, approccioCalcolo, prezzoListino, scontoAcquistoPerc, scontoVenditaPerc, percentualeRicaricoRiga]);

  // Merge di componenti + patchLive + calcolo live di costoUnitario / prezzoVenditaUnitario.
  // Le formule sono identiche a quelle del backend (semantica B, qta e sfrido inclusi).
  const componentiVisibili = useMemo<ComponenteRiga[]>(() => {
    return componenti.map(c => {
      const patch = patchLive[c.id] || {};
      const m = { ...c, ...patch };

      const pl = m.prezzoListino != null && m.prezzoListino !== ''
        ? parseFloat(String(m.prezzoListino))
        : null;
      const q = m.quantita != null && m.quantita !== ''
        ? parseFloat(String(m.quantita))
        : 0;
      const sfrido = m.sfrido != null && m.sfrido !== ''
        ? parseFloat(String(m.sfrido))
        : 0;
      const sa = m.scontoAcquisto != null && m.scontoAcquisto !== ''
        ? parseFloat(String(m.scontoAcquisto))
        : 0;
      const sv = m.scontoVendita != null && m.scontoVendita !== ''
        ? parseFloat(String(m.scontoVendita))
        : 0;

      const qe = q * (1 + sfrido / 100);
      const costoUnitario = pl != null
        ? String(parseFloat((qe * pl * (1 - sa / 100)).toFixed(4)))
        : null;
      const prezzoVenditaUnitario = pl != null
        ? String(parseFloat((qe * pl * (1 - sv / 100)).toFixed(4)))
        : null;

      return { ...m, costoUnitario, prezzoVenditaUnitario };
    });
  }, [componenti, patchLive]);

  // Valori canonici lato server, indicizzati per id componente.
  //
  // Gli input della tabella componenti sono NON controllati (defaultValue), per
  // non rimontare a ogni tasto digitato. Di conseguenza si risincronizzano solo
  // quando React li rimonta. Usiamo questa mappa per dare a ciascun input una
  // `key` legata al valore del server: se il server cambia il valore (cambio
  // articolo, scelta sfrido da tabella, ricalcolo prezzo di listino) l'input si
  // rimonta e mostra il nuovo dato; mentre si digita, invece, cambia solo
  // patchLive e la key resta stabile, quindi il focus non si perde.
  const componentiServer = useMemo(() => {
    const m = new Map<number, ComponenteRiga>();
    componenti.forEach(c => m.set(c.id, c));
    return m;
  }, [componenti]);

  // Calcolato da componenti distinta: somma delle colonne visibili in tabella componenti
  // (P. Vendita un. per SCONTO_DA_LISTINO, Costo un. per RICARICO_SU_COSTI).
  // NOTE: i valori costoUnitario e prezzoVenditaUnitario dei componenti già includono
  // qta e sfrido (semantica B nuova, vedi backend).
  const calcolatoDaComponenti = useMemo<number | null>(() => {
    if (!isConfigurato) return null;
    if (!componentiVisibili || componentiVisibili.length === 0) return null;
    let somma = 0;
    let hasAlmenoUno = false;
    for (const c of componentiVisibili) {
      const campo = approccioCalcolo === 'RICARICO_SU_COSTI'
        ? c.costoUnitario
        : c.prezzoVenditaUnitario;
      if (campo != null && campo !== '') {
        somma += parseFloat(campo);
        hasAlmenoUno = true;
      }
    }
    if (!hasAlmenoUno) return null;
    return parseFloat(somma.toFixed(4));
  }, [isConfigurato, componentiVisibili, approccioCalcolo]);

  // Prezzo calcolato live per ARTICOLO_CONFIGURATO
  // = calcolatoDaComponenti × (1 − scontoSuCalcolato/100)   [SCONTO_DA_LISTINO]
  //   calcolatoDaComponenti × (1 + percentualeRicaricoRiga/100) [RICARICO_SU_COSTI]
  const prezzoCalcolatoConfLive = useMemo<number | null>(() => {
    if (calcolatoDaComponenti == null) return null;
    if (approccioCalcolo === 'RICARICO_SU_COSTI') {
      const r = parseFloat(percentualeRicaricoRiga || '0');
      return parseFloat((calcolatoDaComponenti * (1 + r / 100)).toFixed(4));
    }
    // default: SCONTO_DA_LISTINO (include NESSUNO fallback)
    const s = parseFloat(scontoSuCalcolato || '0');
    return parseFloat((calcolatoDaComponenti * (1 - s / 100)).toFixed(4));
  }, [calcolatoDaComponenti, approccioCalcolo, percentualeRicaricoRiga, scontoSuCalcolato]);

  // Quando attivo override ed il prezzo unitario è vuoto, prepopola col calcolato
  useEffect(() => {
    if (prezzoOverride && !prezzoUnitario && prezzoCalcolatoLive != null) {
      setPrezzoUnitario(String(prezzoCalcolatoLive));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prezzoOverride]);

  // ── Ripartizione live capitolato ──────────────────────────────────────────
  // Il "prezzo effettivo" usato per la ripartizione dipende dal caso:
  //  - override attivo        → prezzoUnitario (input manuale dell'utente)
  //  - DIFFERITA_IN_ORDINE    → prezzoUnitario (prezzo manuale obbligatorio)
  //  - ARTICOLO_CONFIGURATO   → prezzoCalcolatoConfLive (da componenti distinta)
  //  - ARTICOLO               → prezzoCalcolatoLive (da listino + sconti)
  //  - fallback               → prezzoUnitario (già salvato dal backend)
  const ripartizione = useMemo(() => {
    if (!hasCapitolato) return null;

    let p: number;
    if (prezzoOverride || modalitaConfig === 'DIFFERITA_IN_ORDINE') {
      p = parseFloat(prezzoUnitario || '0');
    } else if (isConfigurato && prezzoCalcolatoConfLive != null) {
      p = prezzoCalcolatoConfLive;
    } else if (tipoRiga === 'ARTICOLO' && prezzoCalcolatoLive != null) {
      p = prezzoCalcolatoLive;
    } else {
      p = parseFloat(prezzoUnitario || '0');
    }

    const s = parseFloat(scorporoCapitolato || '0');
    const delta = Math.max(0, p - s);
    const extraAcq = delta * (1 + ricaricoImpresa / 100);
    // Se p < s, l'impresa trattiene l'intero scorporo (s), non p.
    const impImpr = Math.max(s, p) - extraAcq;
    return { delta, extraAcq, impImpr };
  }, [
    hasCapitolato, prezzoUnitario, scorporoCapitolato, ricaricoImpresa,
    prezzoOverride, modalitaConfig, isConfigurato,
    prezzoCalcolatoConfLive, tipoRiga, prezzoCalcolatoLive,
  ]);

  // ── Inizializza componenti ────────────────────────────────────────────────
  const inizializzaDaModello = async () => {
    if (!currentRigaId || !modelloConfigId) return;
    try {
      // @api POST /api/preventivi/:id/righe/:rigaId/init-da-modello -> {"success":true,"data":{"inseriti":"number"}}
      await apiFetch(`/api/preventivi/${preventivoId}/righe/${currentRigaId}/init-da-modello`, {
        method: 'POST',
      });
      showToast('Componenti inizializzati dal modello', 'success');
      await caricaComponenti();
    } catch (e) {
      showToast((e as Error).message || 'Errore inizializzazione', 'error');
    }
  };

  // ── Handlers DA_PRECONFIGURATO ───────────────────────────────────────────
  const applicaPreconfigurato = async (p: PreconfOption): Promise<number | null> => {
    try {
      // @api POST /api/preventivi/:id/righe/crea-da-preconfigurato {"articoloPrecConfiguratoId":"number","raggruppamentoId":"number|null","codiceIvaId":"number|null"} -> {"success":true,"data":{"id":"number","nComponentiInseriti":"number"}}
      const r = await apiFetch<{ success: boolean; data: { id: number; nComponentiInseriti: number } }>(
        `/api/preventivi/${preventivoId}/righe/crea-da-preconfigurato`,
        {
          method: 'POST',
          body: JSON.stringify({
            articoloPrecConfiguratoId: p.id,
            raggruppamentoId: raggruppamentoId,
            codiceIvaId: codiceIvaId,
          }),
        },
      );
      setPreconfSelezionato(p);
      setCurrentRigaId(r.data.id);
      setRigaAppenaCreata(true);
      setArticoloPrecConfId(p.id);
      setDescrizione(p.nome);
      setUmVendita(p.umVenditaSimbolo);
      setArticoloId(p.articoloPadreId);
      setArticoloLabel(`${p.articoloPadreCodice} — ${p.articoloPadreNome}`);
      showToast(`Riga creata da "${p.codice}" con ${r.data.nComponentiInseriti} componenti`, 'success');
      onSaved();
      return r.data.id;
    } catch (e) {
      showToast((e as Error).message || 'Errore creazione riga', 'error');
      return null;
    }
  };

  const onSelezionaPreconfigurato = async (p: PreconfOption) => {
    // Se sto sostituendo (click su "Cambia") e c'è già una riga, gestisci come cambio
    if (sostituendoPreconf && currentRigaId) {
      await onCambiaPreconfigurato(p);
      return;
    }
    await applicaPreconfigurato(p);
  };

  const onCambiaPreconfigurato = async (pNuovo: PreconfOption) => {
    if (!currentRigaId) return;
    const ok = window.confirm(
      'Cambiando preconfigurato, la riga corrente sarà eliminata e ricreata. Tutte le modifiche fatte ai componenti andranno perse. Continuare?'
    );
    if (!ok) { setSostituendoPreconf(false); return; }
    try {
      // @api DELETE /api/preventivi/:id/righe/:rigaId -> {"success":true}
      await apiFetch(`/api/preventivi/${preventivoId}/righe/${currentRigaId}`, { method: 'DELETE' });
      onSaved();
      setCurrentRigaId(null);
      setPreconfSelezionato(null);
      setRigaAppenaCreata(false);
      setComponenti([]);
      setComponentiInterInfo({});
      setSostituendoPreconf(false);
      await applicaPreconfigurato(pNuovo);
    } catch (e) {
      showToast((e as Error).message || 'Errore cambio preconfigurato', 'error');
      setSostituendoPreconf(false);
    }
  };

  const onRimuoviPreconfigurato = async () => {
    if (currentRigaId && rigaAppenaCreata) {
      try {
        // @api DELETE /api/preventivi/:id/righe/:rigaId -> {"success":true}
        await apiFetch(`/api/preventivi/${preventivoId}/righe/${currentRigaId}`, { method: 'DELETE' });
        onSaved();
      } catch { /* silenzioso */ }
    }
    setCurrentRigaId(null);
    setPreconfSelezionato(null);
    setArticoloPrecConfId(null);
    setRigaAppenaCreata(false);
    setDescrizione('');
    setUmVendita('');
    setArticoloId(null);
    setArticoloLabel('');
    setComponenti([]);
    setComponentiInterInfo({});
    setSostituendoPreconf(false);
  };

  const onReimpostaDaPreconfigurato = async () => {
    if (!currentRigaId) return;
    const ok = window.confirm('Reimpostare i componenti dal preconfigurato? Le modifiche ai componenti saranno perse.');
    if (!ok) return;
    try {
      // @api POST /api/preventivi/:id/righe/:rigaId/init-da-preconfigurato -> {"success":true,"data":{"inseriti":"number"}}
      await apiFetch(
        `/api/preventivi/${preventivoId}/righe/${currentRigaId}/init-da-preconfigurato`,
        { method: 'POST' },
      );
      setComponentiInterInfo({});
      await caricaComponenti();
      showToast('Componenti reimpostati dal preconfigurato', 'success');
      onSaved();
    } catch (e) {
      showToast((e as Error).message || 'Errore reimpostazione', 'error');
    }
  };

  // Chiusura modal: se la riga è "fantasma" (appena creata da crea-da-preconfigurato)
  // effettua DELETE per pulire. Se esisteva già (modifica), chiude senza eliminare.
  const handleClose = async () => {
    if (rigaAppenaCreata && currentRigaId) {
      try {
        // @api DELETE /api/preventivi/:id/righe/:rigaId -> {"success":true}
        await apiFetch(`/api/preventivi/${preventivoId}/righe/${currentRigaId}`, { method: 'DELETE' });
        onSaved();
      } catch { /* silenzioso */ }
    }
    onClose();
  };

  // Info intercambiabili per componenti: auto-carica quando i dettagli articolo sono disponibili
  useEffect(() => {
    if (!componenti.length) return;
    const daCaricare: { compId: number; articoloId: number }[] = [];
    componenti.forEach(c => {
      if (c.articoloId == null) return;
      if (c.id in componentiInterInfo) return;
      const art = articoliDettaglioCache[c.articoloId];
      if (!art) return;
      if (art.tipologia !== 'INTERCAMBIABILE') return;
      daCaricare.push({ compId: c.id, articoloId: art.id });
    });
    if (daCaricare.length === 0) return;
    (async () => {
      const nuovi: Record<number, ArticoloIntercambiabileInfo> = {};
      await Promise.all(daCaricare.map(async ({ compId, articoloId: aid }) => {
        try {
          // @api GET /api/anagrafiche/articoli/:id/intercambiabili-con-prezzo-max -> {"success":true,"data":{"isIntercambiabile":"boolean","numeroAlternativi":"number","prezzoMax":"string|null","articoloMaxPrezzo":"{id,codice,descrizioneBase}|null"}}
          const r = await apiFetch<{ success: boolean; data: ArticoloIntercambiabileInfo }>(
            `/api/anagrafiche/articoli/${aid}/intercambiabili-con-prezzo-max`,
          );
          if (r.data) nuovi[compId] = r.data;
        } catch { /* ignora */ }
      }));
      if (Object.keys(nuovi).length > 0) {
        setComponentiInterInfo(prev => ({ ...prev, ...nuovi }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componenti, articoliDettaglioCache]);

  // ── Componenti CRUD ──────────────────────────────────────────────────────
  const aggiungiComponente = async () => {
    if (!currentRigaId) return;
    try {
      // @api POST /api/preventivi/:id/righe/:rigaId/componenti {"tipologia":"string","descrizione":"string","quantita":"number?"} -> {"success":true,"data":{"id":"number"}}
      await apiFetch(`/api/preventivi/${preventivoId}/righe/${currentRigaId}/componenti`, {
        method: 'POST',
        body: JSON.stringify({
          tipologia: 'COMPONENTE',
          descrizione: 'Nuovo componente',
          quantita: 1,
        }),
      });
      await caricaComponenti();
    } catch (e) {
      showToast((e as Error).message || 'Errore aggiunta componente', 'error');
    }
  };

  const aggiornaComponente = async (compId: number, patch: Partial<ComponenteRiga>) => {
    if (!currentRigaId) return;
    try {
      // @api PUT /api/preventivi/:id/righe/:rigaId/componenti/:compId {...} -> {"success":true}
      await apiFetch(
        `/api/preventivi/${preventivoId}/righe/${currentRigaId}/componenti/${compId}`,
        {
          method: 'PUT',
          body: JSON.stringify(patch),
        },
      );
      await caricaComponenti();
      // Dopo il refetch i valori canonici sono quelli del server: una patch
      // locale residua li mascherebbe (es. il prezzo di listino ricalcolato dal
      // backend al cambio articolo).
      setPatchLive(prev => {
        if (!prev[compId]) return prev;
        const next = { ...prev };
        delete next[compId];
        return next;
      });
    } catch (e) {
      showToast((e as Error).message || 'Errore modifica componente', 'error');
    }
  };

  // Aggiorna live la patch (per calcolo immediato) e persiste via aggiornaComponente (backend).
  // Da chiamare in onBlur dei campi componente.
  const aggiornaComponenteLive = async (
    compId: number,
    patch: Partial<ComponenteRiga>,
  ) => {
    // Aggiorna patchLive per feedback visivo immediato fino al caricaComponenti()
    setPatchLive(prev => ({ ...prev, [compId]: { ...(prev[compId] || {}), ...patch } }));
    // Delega al backend (che farà il refetch via caricaComponenti() in aggiornaComponente)
    await aggiornaComponente(compId, patch);
    // Pulisci patchLive per questo componente: i valori canonici ora arrivano dal server
    setPatchLive(prev => {
      const next = { ...prev };
      delete next[compId];
      return next;
    });
  };

  // Riallinea il prezzo di listino del componente all'anagrafica articolo.
  // Il backend ricalcola da solo solo quando cambia l'articolo: se nel frattempo
  // è cambiato il listino del fornitore, il componente resta al prezzo congelato
  // finché non si forza il ricalcolo da qui.
  const ricalcolaPrezzoListino = async (compId: number) => {
    if (!currentRigaId) return;
    setRicalcoloCompId(compId);
    try {
      // @api POST /api/preventivi/:id/righe/:rigaId/componenti/:compId/ricalcola-prezzo-listino -> {"success":true,"data":{"prezzoListino":"number","prezzoPrecedente":"number|null"}}
      const r = await apiFetch<{ success: boolean; data: { prezzoListino: number; prezzoPrecedente: number | null } }>(
        `/api/preventivi/${preventivoId}/righe/${currentRigaId}/componenti/${compId}/ricalcola-prezzo-listino`,
        { method: 'POST' },
      );
      await caricaComponenti();
      setPatchLive(prev => {
        if (!prev[compId]) return prev;
        const next = { ...prev };
        delete next[compId];
        return next;
      });
      const { prezzoListino, prezzoPrecedente } = r.data;
      showToast(
        prezzoPrecedente != null && prezzoPrecedente !== prezzoListino
          ? `Prezzo di listino aggiornato: ${formatCurrency(prezzoPrecedente)} → ${formatCurrency(prezzoListino)}`
          : `Prezzo di listino già allineato (${formatCurrency(prezzoListino)})`,
        'success',
      );
    } catch (e) {
      showToast((e as Error).message || 'Errore nel ricalcolo del prezzo di listino', 'error');
    } finally {
      setRicalcoloCompId(null);
    }
  };

  const eliminaComponente = async (compId: number) => {
    if (!currentRigaId) return;
    if (!window.confirm('Eliminare il componente?')) return;
    try {
      // @api DELETE /api/preventivi/:id/righe/:rigaId/componenti/:compId -> {"success":true}
      await apiFetch(
        `/api/preventivi/${preventivoId}/righe/${currentRigaId}/componenti/${compId}`,
        { method: 'DELETE' },
      );
      await caricaComponenti();
    } catch (e) {
      showToast((e as Error).message || 'Errore eliminazione componente', 'error');
    }
  };

  // ── Salvataggio riga ─────────────────────────────────────────────────────
  const validate = (): string | null => {
    if (!descrizione.trim() && modalitaConfig !== 'DA_PRECONFIGURATO') {
      return 'Descrizione obbligatoria';
    }
    if (tipoRiga === 'ARTICOLO') {
      if (quantita === '') return 'Quantità obbligatoria';

      const qm = parseFloat(articoloSelezionato?.qtaMinOrdinabile || '0');
      const q = parseFloat(quantita || '0');
      if (qm > 0 && q > 0 && Math.abs((q / qm) - Math.round(q / qm)) > 1e-4) {
        return `La quantità deve essere un multiplo di ${formatQuantity(qm)}`;
      }

      if (prezzoOverride && prezzoUnitario === '') {
        return 'Prezzo preventivo obbligatorio quando "Sovrascrivi prezzo" è attivo';
      }
      if (!prezzoOverride && prezzoCalcolatoLive == null) {
        return 'Inserisci prezzo listino e sconto/ricarico per calcolare il prezzo';
      }
    }
    if (tipoRiga === 'ARTICOLO_CONFIGURATO') {
      if (!modalitaConfig) return 'Modalità di configurazione obbligatoria';
      if (modalitaConfig === 'DIFFERITA_IN_ORDINE') {
        if (prezzoUnitario === '' || prezzoUnitario == null) {
          return 'Prezzo unitario obbligatorio per configurazione differita';
        }
        if (parseFloat(prezzoUnitario) < 0) {
          return 'Prezzo unitario non può essere negativo';
        }
      }
    }
    if (tipoRiga === 'A_CORPO') {
      if (!articoloId) return 'Articolo padre obbligatorio per riga a corpo';
      if (!prezzoUnitario || Number(prezzoUnitario) <= 0) return 'Prezzo obbligatorio per riga a corpo';
      if (!quantita || Number(quantita) <= 0) return 'Quantità obbligatoria';
    }
    if (tipoRiga === 'RICORRENTE') {
      if (!articoloId) return 'Articolo obbligatorio per riga ricorrente';
      if (!periodicita) return 'Periodicità di fatturazione obbligatoria';
      if (!modalitaPagamento) return 'Modalità di determinazione importo obbligatoria';
      if (modalitaPagamento === 'A_PREVENTIVO') {
        if (!prezzoUnitario || Number(prezzoUnitario) <= 0) return 'Prezzo obbligatorio per modalità A preventivo';
      }
      if (modalitaPagamento === 'A_CONSUNTIVO_VOLUMI') {
        if (!tariffaUnitaria || Number(tariffaUnitaria) <= 0) return 'Tariffa unitaria obbligatoria';
      }
      if (modalitaPagamento === 'A_CONSUNTIVO_TRANSAZIONI') {
        if (tariffaFissaTransazione === '' || Number(tariffaFissaTransazione) < 0) return 'Tariffa fissa per transazione obbligatoria';
        if (!percentualeTransazione || Number(percentualeTransazione) <= 0) return 'Percentuale sul transato obbligatoria';
      }
    }
    if ((tipoRigaCap === 'BASE_CAPITOLATO' || tipoRigaCap === 'EXTRA_CAPITOLATO_SOSTITUTIVO') && !offertaCapRigaId && !offertaCapRigaAccessoriaId) {
      return 'Seleziona una riga capitolato di riferimento';
    }
    if (isBaseCapitolato && (quantita === '' || Number(quantita) <= 0)) {
      return 'Quantità obbligatoria';
    }
    return null;
  };

  const buildBody = (): Record<string, unknown> => {
    const body: Record<string, unknown> = {
      tipoRiga,
      raggruppamentoId,
      descrizione: descrizione.trim(),
      descrizioneEstesa: descrizioneEstesa || null,
      umVendita: umVendita || null,
      tipoRigaCapitolato: tipoRigaCap,
    };
    if (!isDescrittiva) {
      body.articoloId = articoloId;
      body.quantita = parseNumOrNull(quantita);
      body.prezzoUnitario = parseNumOrNull(prezzoUnitario);
      body.sconto = parseNumOrNull(sconto);
      body.codiceIvaId = codiceIvaId;
    }
    if (isConfigurato) {
      body.modalitaConfigurazione = modalitaConfig;
      body.modelloConfigurazioneId = modelloConfigId;
      body.articoloPrecConfiguratoId = articoloPrecConfId;

      if (isBaseCapitolato) {
        // Per BASE_CAPITOLATO il prezzo è fissato dal capitolato, nessun calcolo automatico
        body.approccioCalcolo = 'NESSUNO';
        body.percentualeRicaricoRiga = null;
        body.scontoSuCalcolato = null;
        body.prezzoSovrascritto = true;
        body.prezzoUnitario = parseNumOrNull(prezzoUnitario);
      } else if (modalitaConfig === 'DIFFERITA_IN_ORDINE') {
        // Prezzo manuale vincolante, nessun calcolo automatico.
        body.approccioCalcolo = 'NESSUNO';
        body.percentualeRicaricoRiga = null;
        body.scontoSuCalcolato = null;
        body.prezzoSovrascritto = true;
        body.prezzoUnitario = parseNumOrNull(prezzoUnitario);
      } else {
        body.approccioCalcolo = approccioCalcolo === 'NESSUNO' ? 'SCONTO_DA_LISTINO' : approccioCalcolo;
        body.percentualeRicaricoRiga = parseNumOrNull(percentualeRicaricoRiga);
        body.scontoSuCalcolato = parseNumOrNull(scontoSuCalcolato);
        body.prezzoSovrascritto = prezzoOverride;
      }
    } else if (tipoRiga === 'ARTICOLO') {
      body.sconto = null;
      if (!prezzoOverride) {
        body.prezzoUnitario = null;
      }
      body.approccioCalcolo = approccioCalcolo === 'NESSUNO' ? 'SCONTO_DA_LISTINO' : approccioCalcolo;
      body.percentualeRicaricoRiga = parseNumOrNull(percentualeRicaricoRiga);
      body.prezzoListino = parseNumOrNull(prezzoListino);
      body.scontoAcquistoCodice = scontoAcquistoCodice.trim() || null;
      body.scontoAcquistoPercentuale = parseNumOrNull(scontoAcquistoPerc);
      body.scontoVenditaCodice = scontoVenditaCodice.trim() || null;
      body.scontoVenditaPercentuale = parseNumOrNull(scontoVenditaPerc);
      body.prezzoSovrascritto = prezzoOverride;
    }
    if (tipoRigaCap === 'BASE_CAPITOLATO' || tipoRigaCap === 'EXTRA_CAPITOLATO_SOSTITUTIVO') {
      body.offertaCapRigaId = offertaCapRigaId;
      body.offertaCapRigaAccessoriaId = offertaCapRigaAccessoriaId;
      body.scorporoCapitolato = parseNumOrNull(scorporoCapitolato);
    } else {
      // EXTRA_CAPITOLATO_AGGIUNTIVO
      body.offertaCapRigaId = null;
      body.offertaCapRigaAccessoriaId = null;
      body.scorporoCapitolato = null;
    }

    // ── Nodo 1: campi specifici A_CORPO / RICORRENTE ─────────────────────
    if (tipoRiga === 'A_CORPO') {
      body.prezzoSovrascritto = true;
      body.approccioCalcolo = 'NESSUNO';
    }
    if (tipoRiga === 'RICORRENTE') {
      body.periodicita = periodicita || null;
      body.durataMesi = durataMesi ? Number(durataMesi) : null;
      body.modalitaPagamento = modalitaPagamento || null;
      body.dataInizio = dataInizio || null;
      body.dataFine = dataFine || null;
      body.tariffaUnitaria =
        (modalitaPagamento === 'A_CONSUNTIVO_VOLUMI' && tariffaUnitaria)
          ? Number(tariffaUnitaria) : null;
      body.tariffaFissaTransazione =
        (modalitaPagamento === 'A_CONSUNTIVO_TRANSAZIONI' && tariffaFissaTransazione !== '')
          ? Number(tariffaFissaTransazione) : null;
      body.percentualeTransazione =
        (modalitaPagamento === 'A_CONSUNTIVO_TRANSAZIONI' && percentualeTransazione)
          ? Number(percentualeTransazione) : null;
      // sottotipoRicorrenza: il backend lo deriva (e valida) dalla tipologia articolo
      body.prezzoSovrascritto = true;
      body.approccioCalcolo = 'NESSUNO';
    }

    return body;
  };

  const salva = async () => {
    const err = validate();
    if (err) {
      showToast(err, 'error');
      return;
    }
    setSaving(true);
    try {
      const body = buildBody();
      if (currentRigaId) {
        // @api PUT /api/preventivi/:id/righe/:rigaId {...} -> {"success":true}
        await apiFetch(`/api/preventivi/${preventivoId}/righe/${currentRigaId}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });

        // Gestione alternative (solo in edit di riga in raggruppamento)
        if (rigaEsistente && rigaEsistente.raggruppamentoId && rigaEsistente.tipoRiga !== 'DESCRITTIVA') {
          const wasInCluster = !!rigaEsistente.alternativaGruppo;
          const oldEtichetta = rigaEsistente.etichettaAlternativa || '';
          const newEtichetta = altEtichetta.trim();

          if (altTargetId === null && wasInCluster) {
            // Slega
            // @api POST /api/preventivi/:id/righe/:rigaId/imposta-alternativa {"rigaTargetId":"null"} -> {"success":true}
            await apiFetch(`/api/preventivi/${preventivoId}/righe/${rigaEsistente.id}/imposta-alternativa`, {
              method: 'POST',
              body: JSON.stringify({ rigaTargetId: null }),
            });
          } else if (altTargetId === -2) {
            // Resta nel cluster: aggiorna solo l'etichetta se cambiata
            if (oldEtichetta !== newEtichetta) {
              // @api PUT /api/preventivi/:id/righe/:rigaId {"etichettaAlternativa":"string|null"} -> {"success":true}
              await apiFetch(`/api/preventivi/${preventivoId}/righe/${rigaEsistente.id}`, {
                method: 'PUT',
                body: JSON.stringify({ etichettaAlternativa: newEtichetta || null }),
              });
            }
          } else if (altTargetId === -1) {
            // Nuovo cluster: trova la prima riga candidata
            const candidate = righeDelPreventivo.find(r =>
              r.id !== rigaEsistente.id &&
              r.raggruppamentoId === rigaEsistente.raggruppamentoId &&
              r.tipoRiga !== 'DESCRITTIVA'
            );
            if (candidate) {
              // @api POST /api/preventivi/:id/righe/:rigaId/imposta-alternativa {"rigaTargetId":"number","etichettaAlternativa":"string|null"} -> {"success":true}
              await apiFetch(`/api/preventivi/${preventivoId}/righe/${rigaEsistente.id}/imposta-alternativa`, {
                method: 'POST',
                body: JSON.stringify({ rigaTargetId: candidate.id, etichettaAlternativa: newEtichetta || null }),
              });
            } else {
              showToast('Nessuna riga disponibile per creare il cluster', 'error');
            }
          } else if (altTargetId != null && altTargetId > 0) {
            // Lega al cluster della target scelta
            await apiFetch(`/api/preventivi/${preventivoId}/righe/${rigaEsistente.id}/imposta-alternativa`, {
              method: 'POST',
              body: JSON.stringify({ rigaTargetId: altTargetId, etichettaAlternativa: newEtichetta || null }),
            });
          }
        }

        showToast('Riga aggiornata', 'success');
        setRigaAppenaCreata(false);
        onSaved();
        onClose();
      } else {
        // @api POST /api/preventivi/:id/righe {...} -> {"success":true,"data":{"id":"number"}}
        const r = await apiFetch<{ success: boolean; data: { id: number } }>(
          `/api/preventivi/${preventivoId}/righe`,
          { method: 'POST', body: JSON.stringify(body) },
        );
        showToast('Riga creata', 'success');
        if (isConfigurato && r.data?.id) {
          // Lascio la modale aperta per configurare i componenti
          setCurrentRigaId(r.data.id);
          onSaved();
        } else {
          onSaved();
          onClose();
        }
      }
    } catch (e) {
      showToast((e as Error).message || 'Errore salvataggio', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="prev-modal">
      <div className="prev-modal__box prev-modal__box--lg mcr-box">
        <div className="prev-modal__header">
          <h2 className="prev-modal__title">
            {currentRigaId ? 'Modifica riga' : 'Aggiungi riga'}
          </h2>
          <button className="prev-modal__close" onClick={handleClose}>
            <XIcon />
          </button>
        </div>

        <div className="prev-modal__body mcr-body">
          {/* ── Sezione 1: Link capitolato (guida i passi successivi) ──────── */}
          {hasCapitolato && !isDescrittiva && (
            <div className="mcr-section">
              <div className="mcr-section__title">1. Link capitolato</div>
              {preventivo.offertaCapitolato && (
                <div className="mcr-info">
                  Capitolato: <strong>{preventivo.offertaCapitolato.codice}</strong> — Ricarico
                  impresa {preventivo.ricaricoImpresaFreeze || '0'}%
                </div>
              )}
              <div className="mcr-tipo-cap-radio-group">
                <label className="mcr-radio-pill">
                  <input
                    type="radio"
                    name="tipoCap"
                    value="BASE_CAPITOLATO"
                    checked={tipoRigaCap === 'BASE_CAPITOLATO'}
                    onChange={() => onCambiaTipoCap('BASE_CAPITOLATO')}
                  />
                  <span>Base capitolato</span>
                </label>
                <label className="mcr-radio-pill">
                  <input
                    type="radio"
                    name="tipoCap"
                    value="EXTRA_CAPITOLATO_SOSTITUTIVO"
                    checked={tipoRigaCap === 'EXTRA_CAPITOLATO_SOSTITUTIVO'}
                    onChange={() => onCambiaTipoCap('EXTRA_CAPITOLATO_SOSTITUTIVO')}
                  />
                  <span>Extra — sostituzione</span>
                </label>
                <label className="mcr-radio-pill">
                  <input
                    type="radio"
                    name="tipoCap"
                    value="EXTRA_CAPITOLATO_AGGIUNTIVO"
                    checked={tipoRigaCap === 'EXTRA_CAPITOLATO_AGGIUNTIVO'}
                    onChange={() => onCambiaTipoCap('EXTRA_CAPITOLATO_AGGIUNTIVO')}
                  />
                  <span>Extra — aggiunta</span>
                </label>
              </div>

              {/* BASE: selezione riga + quantità (prezzo e componenti sono auto-popolati) */}
              {tipoRigaCap === 'BASE_CAPITOLATO' && (
                <>
                  <div className="bop-form-group">
                    <label className="bop-label">Riga capitolato di riferimento</label>
                    <select
                      className="bop-select"
                      value={offertaCapRigaId ? `riga-${offertaCapRigaId}` : offertaCapRigaAccessoriaId ? `acc-${offertaCapRigaAccessoriaId}` : ''}
                      onChange={e => onSelezionaRigaCapKey(e.target.value)}
                    >
                      <option value="">— Seleziona —</option>
                      {righeCapitolato.map(r => (
                        <option key={`${r.isAccessoria ? 'acc' : 'riga'}-${r.id}`} value={`${r.isAccessoria ? 'acc' : 'riga'}-${r.id}`}>
                          {r.descrizioneRiga || r.nomePrecopy} — € {formatCurrency(parseFloat(r.prezzoUnitario))} ({r.codicePrecopy})
                        </option>
                      ))}
                    </select>
                    {!currentRigaId && (
                      <div className="mcr-note">
                        Seleziona la riga: la configurazione verrà caricata automaticamente.
                      </div>
                    )}
                  </div>
                  {currentRigaId && (
                    <>
                      <div className="bop-form-row">
                        <div className="bop-form-group">
                          <label className="bop-label">U.M.</label>
                          <input
                            className="bop-input"
                            value={umVendita}
                            readOnly
                            disabled
                            title="U.M. ereditata dall'anagrafica articolo"
                          />
                        </div>
                        <div className="bop-form-group">
                          <label className="bop-label">Quantità</label>
                          <input
                            className="bop-input"
                            type="number"
                            step="0.01"
                            min="0"
                            value={quantita}
                            onChange={e => setQuantita(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="mcr-info mcr-info--success">
                        Riga creata. Modifica i componenti nella sezione sottostante.
                      </div>
                    </>
                  )}
                </>
              )}

              {/* EXTRA_SOSTITUTIVO: riga + scorporo */}
              {tipoRigaCap === 'EXTRA_CAPITOLATO_SOSTITUTIVO' && (
                <div className="mcr-grid-2">
                  <div className="bop-form-group">
                    <label className="bop-label">Riga capitolato di riferimento</label>
                    <select
                      className="bop-select"
                      value={offertaCapRigaId ? `riga-${offertaCapRigaId}` : offertaCapRigaAccessoriaId ? `acc-${offertaCapRigaAccessoriaId}` : ''}
                      onChange={e => onSelezionaRigaCapKey(e.target.value)}
                    >
                      <option value="">— Seleziona —</option>
                      {righeCapitolato.map(r => (
                        <option key={`${r.isAccessoria ? 'acc' : 'riga'}-${r.id}`} value={`${r.isAccessoria ? 'acc' : 'riga'}-${r.id}`}>
                          {r.descrizioneRiga || r.nomePrecopy} — € {formatCurrency(parseFloat(r.prezzoUnitario))} ({r.codicePrecopy})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Scorporo capitolato (€)</label>
                    <input
                      className="bop-input"
                      type="number"
                      step="0.01"
                      value={scorporoCapitolato}
                      onChange={e => setScorporoCapitolato(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {tipoRigaCap === 'EXTRA_CAPITOLATO_AGGIUNTIVO' && (
                <div className="mcr-info">
                  Tutto il prezzo sarà extra per l'acquirente (scorporo forzato a 0).
                </div>
              )}
            </div>
          )}

          {/* ── Sezione 2: Tipo riga (nascosta se BASE_CAPITOLATO) ─────────── */}
          {!isBaseCapitolato && (
          <div className="mcr-section">
            <div className="mcr-section__title">{hasCapitolato ? '2. Tipo riga' : '1. Tipo riga'}</div>
            <div className="mcr-radio-group">
              <label className="mcr-radio-item">
                <input
                  type="radio"
                  name="tipoRiga"
                  value="ARTICOLO"
                  checked={tipoRiga === 'ARTICOLO'}
                  onChange={() => setTipoRiga('ARTICOLO')}
                  disabled={!isNew}
                />
                <div className="mcr-radio-item__body">
                  <div className="mcr-radio-item__title">Articolo</div>
                  <div className="mcr-radio-item__help">
                    Riga standard con un singolo articolo (o libera)
                  </div>
                </div>
              </label>
              {!isTipoRigaNascosto('ARTICOLO_CONFIGURATO') && (
              <label className="mcr-radio-item">
                <input
                  type="radio"
                  name="tipoRiga"
                  value="ARTICOLO_CONFIGURATO"
                  checked={tipoRiga === 'ARTICOLO_CONFIGURATO'}
                  onChange={() => setTipoRiga('ARTICOLO_CONFIGURATO')}
                  disabled={!isNew}
                />
                <div className="mcr-radio-item__body">
                  <div className="mcr-radio-item__title">Articolo configurato</div>
                  <div className="mcr-radio-item__help">
                    Riga configurata con componenti e calcolo prezzo
                  </div>
                </div>
              </label>
              )}
              <label className="mcr-radio-item">
                <input
                  type="radio"
                  name="tipoRiga"
                  value="A_CORPO"
                  checked={tipoRiga === 'A_CORPO'}
                  onChange={() => setTipoRiga('A_CORPO')}
                  disabled={!isNew}
                />
                <div className="mcr-radio-item__body">
                  <div className="mcr-radio-item__title">Riga a corpo</div>
                  <div className="mcr-radio-item__help">
                    Pacchetto a prezzo unico (es. "bagno chiavi in mano"). I figli si
                    definiscono nella validazione tecnica dell'ordine.
                  </div>
                </div>
              </label>
              {!isTipoRigaNascosto('RICORRENTE') && (
              <label className="mcr-radio-item">
                <input
                  type="radio"
                  name="tipoRiga"
                  value="RICORRENTE"
                  checked={tipoRiga === 'RICORRENTE'}
                  onChange={() => setTipoRiga('RICORRENTE')}
                  disabled={!isNew}
                />
                <div className="mcr-radio-item__body">
                  <div className="mcr-radio-item__title">Riga ricorrente</div>
                  <div className="mcr-radio-item__help">
                    Licenze, canoni, consumi, commissioni — fatturati periodicamente.
                  </div>
                </div>
              </label>
              )}
              <label className="mcr-radio-item">
                <input
                  type="radio"
                  name="tipoRiga"
                  value="DESCRITTIVA"
                  checked={tipoRiga === 'DESCRITTIVA'}
                  onChange={() => setTipoRiga('DESCRITTIVA')}
                  disabled={!isNew}
                />
                <div className="mcr-radio-item__body">
                  <div className="mcr-radio-item__title">Descrittiva</div>
                  <div className="mcr-radio-item__help">Solo testo, nessun prezzo</div>
                </div>
              </label>
            </div>
            {!isNew && (
              <div className="mcr-note">Il tipo riga non è modificabile dopo la creazione.</div>
            )}
          </div>
          )}

          {/* Raggruppamento (sempre visibile) */}
          <div className="mcr-section">
            <div className="bop-form-group">
              <label className="bop-label">Raggruppamento</label>
              <select
                className="bop-select"
                value={raggruppamentoId ?? ''}
                onChange={e =>
                  setRaggruppamentoId(e.target.value ? parseInt(e.target.value, 10) : null)
                }
              >
                <option value="">— Nessuno —</option>
                {(() => {
                  // Costruisce path "Zona giorno › Cucina" e ordina ricorsivamente
                  const byId = new Map(raggruppamenti.map(r => [r.id, r]));
                  const labelOf = (r: RaggruppamentoLite): string => {
                    const parts: string[] = [r.etichetta];
                    let cur = r.parentId != null ? byId.get(r.parentId) : undefined;
                    while (cur) {
                      parts.unshift(cur.etichetta);
                      cur = cur.parentId != null ? byId.get(cur.parentId) : undefined;
                    }
                    return parts.join(' › ');
                  };
                  return [...raggruppamenti]
                    .sort((a, b) => labelOf(a).localeCompare(labelOf(b), 'it'))
                    .map(r => (
                      <option key={r.id} value={r.id}>
                        {r.livello ? `[L${r.livello}] ` : ''}{labelOf(r)}
                      </option>
                    ));
                })()}
              </select>
            </div>
          </div>

          {/* ── Sezione 3: Modalità di configurazione (nascosta se BASE_CAPITOLATO) */}
          {isConfigurato && !isBaseCapitolato && (
            <div className="mcr-section">
              <div className="mcr-section__title">{hasCapitolato ? '3. Modalità di configurazione' : '2. Modalità di configurazione'}</div>
              <div className="mcr-radio-group">
                <label className="mcr-radio-item">
                  <input
                    type="radio"
                    name="modalita"
                    value="DA_MODELLO"
                    checked={modalitaConfig === 'DA_MODELLO'}
                    onChange={() => setModalitaConfig('DA_MODELLO')}
                  />
                  <div className="mcr-radio-item__body">
                    <div className="mcr-radio-item__title">Parto da un modello</div>
                    <div className="mcr-radio-item__help">
                      Esempio: modello mobile da bagno
                    </div>
                  </div>
                </label>
                <label className="mcr-radio-item">
                  <input
                    type="radio"
                    name="modalita"
                    value="DA_PRECONFIGURATO"
                    checked={modalitaConfig === 'DA_PRECONFIGURATO'}
                    onChange={() => setModalitaConfig('DA_PRECONFIGURATO')}
                  />
                  <div className="mcr-radio-item__body">
                    <div className="mcr-radio-item__title">Parto da un pre-configurato</div>
                    <div className="mcr-radio-item__help">Esempio: bagno completo</div>
                  </div>
                </label>
                <label className="mcr-radio-item">
                  <input
                    type="radio"
                    name="modalita"
                    value="DIFFERITA_IN_ORDINE"
                    checked={modalitaConfig === 'DIFFERITA_IN_ORDINE'}
                    onChange={() => setModalitaConfig('DIFFERITA_IN_ORDINE')}
                  />
                  <div className="mcr-radio-item__body">
                    <div className="mcr-radio-item__title">Configurazione differita</div>
                    <div className="mcr-radio-item__help">
                      Sarà completata nell'ordine (prezzo manuale)
                    </div>
                  </div>
                </label>
              </div>

              {modalitaConfig === 'DA_MODELLO' && (
                <div className="mcr-grid-2">
                  <div className="bop-form-group">
                    <label className="bop-label">Modello configurazione</label>
                    <select
                      className="bop-select"
                      value={modelloConfigId ?? ''}
                      onChange={e =>
                        setModelloConfigId(e.target.value ? parseInt(e.target.value, 10) : null)
                      }
                    >
                      <option value="">— Seleziona —</option>
                      {modelli.map(m => (
                        <option key={m.id} value={m.id}>
                          {m.codice} — {m.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">&nbsp;</label>
                    <button
                      className="bop-btn bop-btn-secondary"
                      onClick={inizializzaDaModello}
                      disabled={!currentRigaId || !modelloConfigId}
                      title={
                        !currentRigaId
                          ? 'Salva prima la riga'
                          : !modelloConfigId
                          ? 'Seleziona un modello'
                          : ''
                      }
                    >
                      Inizializza componenti
                    </button>
                  </div>
                </div>
              )}

              {modalitaConfig === 'DA_PRECONFIGURATO' && (
                <div className="bop-form-group">
                  <label className="bop-label">Articolo pre-configurato</label>
                  {preconfSelezionato && !sostituendoPreconf ? (
                    <div className="prev-mod-preconf-selected">
                      <div className="prev-mod-preconf-selected__main">
                        <span className="bop-badge bop-badge--success">{preconfSelezionato.codice}</span>
                        <div>
                          <div className="prev-mod-preconf-selected__nome">{preconfSelezionato.nome}</div>
                          <div className="prev-mod-preconf-selected__meta">
                            Padre: <strong>{preconfSelezionato.articoloPadreCodice}</strong> — {preconfSelezionato.articoloPadreNome}
                            {' · '}UM {preconfSelezionato.umVenditaSimbolo}
                            {' · '}{preconfSelezionato.nComponenti} componenti
                          </div>
                        </div>
                      </div>
                      <div className="prev-mod-preconf-selected__actions">
                        <button
                          type="button"
                          className="bop-btn bop-btn-secondary"
                          onClick={() => setSostituendoPreconf(true)}
                          title="Cambia preconfigurato (la riga verrà ricreata)"
                        >
                          Cambia
                        </button>
                        <button
                          type="button"
                          className="bop-btn bop-btn-secondary"
                          onClick={onReimpostaDaPreconfigurato}
                          title="Reimposta i componenti dal preconfigurato"
                        >
                          Reimposta
                        </button>
                        <button
                          type="button"
                          className="bop-btn-icon"
                          onClick={onRimuoviPreconfigurato}
                          title="Rimuovi preconfigurato"
                        >
                          <XIcon />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <AutocompletePreconfigurato
                      societaId={preventivo.societaId}
                      onSeleziona={onSelezionaPreconfigurato}
                      onAnnullaSostituzione={sostituendoPreconf ? () => setSostituendoPreconf(false) : undefined}
                    />
                  )}
                </div>
              )}

              {modalitaConfig === 'DIFFERITA_IN_ORDINE' && (
                <div className="mcr-info">
                  Al momento della conversione in ordine, questa riga sarà marcata come
                  configurazione da completare. Potrai comunque impostare un prezzo manualmente
                  ora.
                </div>
              )}
            </div>
          )}

          {/* ── Sezione 4: Articolo / Descrizione (nascosta se BASE_CAPITOLATO) */}
          {!isBaseCapitolato && (
          <div className="mcr-section">
            <div className="mcr-section__title">
              {isDescrittiva ? '2. Descrizione' : (hasCapitolato ? '4. Articolo / Descrizione' : '3. Articolo / Descrizione')}
            </div>

            {!isDescrittiva && (
              <>
                <div className="bop-form-group mcr-articolo-picker">
                  <label className="bop-label">Articolo</label>
                  {articoloId && !articoloSearchOpen ? (
                    <>
                      <div className="mcr-articolo-selected">
                        <span>{articoloLabel || 'Articolo selezionato'}</span>
                        {!(isConfigurato && modalitaConfig === 'DA_PRECONFIGURATO') && (
                          <div className="prev-mod-articolo-actions">
                            <button
                              type="button"
                              className="bop-btn bop-btn-secondary"
                              onClick={() => {
                                setArticoloId(null);
                                setArticoloLabel('');
                                setArticoloSelezionato(null);
                                setArticoloInterInfo(null);
                                setArticoloSearchOpen(true);
                              }}
                            >
                              Cambia
                            </button>
                            <button
                              type="button"
                              className="bop-btn bop-btn-secondary"
                              onClick={() => articoloId && window.open(`/anagrafiche/articoli/${articoloId}`, '_blank', 'noopener,noreferrer')}
                              title="Apre la scheda articolo in una nuova tab"
                            >
                              Gestisci articolo
                            </button>
                            <button
                              type="button"
                              className="bop-btn bop-btn-secondary"
                              onClick={handleAggiornaArticolo}
                              disabled={!articoloId}
                              title="Rilegge u.m. e prezzo listino dall'anagrafica"
                            >
                              Aggiorna
                            </button>
                          </div>
                        )}
                      </div>
                      {articoloSelezionato && (
                        <>
                          <small className="bop-text-muted prev-mod-articolo-riepilogo">
                            {[
                              articoloSelezionato.tipologia
                                ? `Tipologia: ${articoloSelezionato.tipologia.replace(/_/g, ' ')}`
                                : null,
                              articoloSelezionato.fornitore
                                ? `Fornitore: ${articoloSelezionato.fornitore.ragioneSociale || articoloSelezionato.fornitore.nome || '—'}`
                                : null,
                              articoloSelezionato.marca?.nome ? `Marca: ${articoloSelezionato.marca.nome}` : null,
                              articoloSelezionato.collezione?.nome ? `Collezione: ${articoloSelezionato.collezione.nome}` : null,
                              articoloSelezionato.formato?.nome ? `Formato: ${articoloSelezionato.formato.nome}` : null,
                              articoloSelezionato.categoriaMerceologica?.nome
                                ? `Cat. merc.: ${articoloSelezionato.categoriaMerceologica.nome}`
                                : null,
                              articoloSelezionato.qtaMinOrdinabile != null && parseFloat(articoloSelezionato.qtaMinOrdinabile) > 0
                                ? `Qta min. ordinabile: ${formatQuantity(parseFloat(articoloSelezionato.qtaMinOrdinabile))} ${articoloSelezionato.umVendita?.simbolo || ''}`
                                : null,
                            ].filter(Boolean).join(' · ')}
                          </small>

                          {articoloInterInfo?.isIntercambiabile && articoloInterInfo.numeroAlternativi === 0 && (
                            <div className="prev-mod-intercambiabile-hint prev-mod-intercambiabile-hint--warning">
                              Articolo intercambiabile senza alternativi associati.
                              Definisci gli alternativi in <strong>Archivi &rarr; Articoli intercambiabili</strong>
                              per abilitare il calcolo del prezzo max.
                            </div>
                          )}
                          {articoloInterInfo?.isIntercambiabile && articoloInterInfo.numeroAlternativi > 0 && articoloInterInfo.articoloMaxPrezzo && (
                            <div className="prev-mod-intercambiabile-hint prev-mod-intercambiabile-hint--info">
                              Articolo intercambiabile &mdash; prezzo listino precompilato con il massimo
                              tra gli alternativi: <strong>&euro; {formatCurrency(parseFloat(articoloInterInfo.prezzoMax || '0'))}</strong>{' '}
                              (<code>{articoloInterInfo.articoloMaxPrezzo.codice}</code> &mdash; {articoloInterInfo.articoloMaxPrezzo.descrizioneBase})
                            </div>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <input
                        className="bop-input"
                        placeholder="Cerca articolo per codice o descrizione..."
                        value={articoloSearch}
                        onChange={e => setArticoloSearch(e.target.value)}
                        onFocus={() => setArticoloSearchOpen(true)}
                      />
                      {isConfigurato && modalitaConfig === 'DIFFERITA_IN_ORDINE' && (
                        <small className="bop-text-muted">
                          Configurazione differita: vengono mostrati solo articoli di tipologia <strong>Prodotto configurato</strong>.
                        </small>
                      )}
                      {articoloSearchOpen && articoliFiltratiPerRadio.length > 0 && (
                        <div className="mcr-articolo-results">
                          {articoliFiltratiPerRadio.map(a => (
                            <button
                              key={a.id}
                              type="button"
                              className="mcr-articolo-result"
                              onClick={() => onSelezionaArticolo(a)}
                            >
                              <div className="mcr-articolo-result__line1">
                                <span className="mcr-articolo-result__codice">{a.codice}</span>
                                {a.tipologia && (
                                  <span className="bop-badge bop-badge-neutral prev-mod-tipologia-badge">
                                    {a.tipologia.replace(/_/g, ' ')}
                                  </span>
                                )}
                                <span className="mcr-articolo-result__desc">{a.descrizioneBase}</span>
                              </div>
                              <div className="mcr-articolo-result__line2">
                                {a.umVendita?.simbolo && (
                                  <span className="mcr-articolo-result__meta">u.m. {a.umVendita.simbolo}</span>
                                )}
                                {a.categoriaMerceologica?.nome && (
                                  <span className="mcr-articolo-result__meta">{a.categoriaMerceologica.nome}</span>
                                )}
                                {a.marca?.nome && (
                                  <span className="mcr-articolo-result__meta">{a.marca.nome}</span>
                                )}
                                {a.prezzoListinoVendita && (
                                  <span className="mcr-articolo-result__price">
                                    € {formatCurrency(parseFloat(a.prezzoListinoVendita))}
                                    {a.umVendita?.simbolo ? ` / ${a.umVendita.simbolo}` : ''}
                                  </span>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {articoloSearchOpen && (
                        <button
                          type="button"
                          className="bop-btn bop-btn-secondary mcr-articolo-skip"
                          onClick={() => setArticoloSearchOpen(false)}
                        >
                          Salta (riga libera)
                        </button>
                      )}
                    </>
                  )}
                </div>
              </>
            )}

            <div className="bop-form-group">
              <label className="bop-label">Descrizione *</label>
              <input
                className="bop-input"
                value={descrizione}
                onChange={e => setDescrizione(e.target.value)}
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Descrizione estesa</label>
              <textarea
                className="bop-input"
                rows={3}
                value={descrizioneEstesa}
                onChange={e => setDescrizioneEstesa(e.target.value)}
              />
            </div>

            {!isDescrittiva && isConfigurato && (
              <>
                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">U.M.</label>
                    <input
                      className="bop-input"
                      value={umVendita}
                      readOnly
                      disabled
                      title="U.M. ereditata dall'anagrafica articolo"
                    />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Quantità</label>
                    <input
                      className="bop-input"
                      type="number"
                      step="0.01"
                      value={quantita}
                      onChange={e => setQuantita(e.target.value)}
                    />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Sconto %</label>
                    <input
                      className="bop-input"
                      type="number"
                      step="0.01"
                      value={sconto}
                      onChange={e => setSconto(e.target.value)}
                      disabled={modalitaConfig !== 'DIFFERITA_IN_ORDINE'}
                      readOnly={modalitaConfig !== 'DIFFERITA_IN_ORDINE'}
                    />
                    {modalitaConfig !== 'DIFFERITA_IN_ORDINE' && (
                      <small className="bop-text-muted">
                        Lo sconto è editabile solo per configurazione differita. Negli altri casi lo sconto si gestisce sul prezzo finale.
                      </small>
                    )}
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Codice IVA</label>
                    <select
                      className="bop-select"
                      value={codiceIvaId ?? ''}
                      onChange={e =>
                        setCodiceIvaId(e.target.value ? parseInt(e.target.value, 10) : null)
                      }
                    >
                      <option value="">— Seleziona —</option>
                      {codiciIva.map(iva => (
                        <option key={iva.id} value={iva.id}>
                          {iva.codice} — {iva.descrizione} ({formatNumber(parseFloat(iva.aliquota))}%)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}

            {!isConfigurato && tipoRiga === 'ARTICOLO' && (
              <>
                {/* Riga 1: UM readonly + Quantità + Sconto vendita % + Codice IVA */}
                <div className="bop-form-row prev-mod-riga-quantity">
                  <div className="bop-form-group">
                    <label className="bop-label">U.M.</label>
                    <input
                      className="bop-input"
                      value={umVendita}
                      readOnly
                      disabled
                      title="U.M. ereditata dall'anagrafica articolo"
                    />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Quantità</label>
                    <div className="prev-mod-quantita-wrap">
                      <button
                        type="button"
                        className="bop-btn-icon"
                        onClick={() => {
                          const qm = parseFloat(articoloSelezionato?.qtaMinOrdinabile || '0');
                          const step = qm > 0 ? qm : 1;
                          const cur = parseFloat(quantita || '0');
                          const next = Math.max(0, cur - step);
                          setQuantita(next > 0 ? String(parseFloat(next.toFixed(4))) : '');
                        }}
                        disabled={!quantita || parseFloat(quantita) <= 0}
                        title="Diminuisci"
                      >–</button>
                      <input
                        className="bop-input"
                        type="number"
                        step={articoloSelezionato?.qtaMinOrdinabile && parseFloat(articoloSelezionato.qtaMinOrdinabile) > 0
                          ? String(parseFloat(articoloSelezionato.qtaMinOrdinabile))
                          : '0.0001'}
                        min="0"
                        value={quantita}
                        onChange={e => setQuantita(e.target.value)}
                        onBlur={() => {
                          const qm = parseFloat(articoloSelezionato?.qtaMinOrdinabile || '0');
                          if (qm > 0 && quantita) {
                            const q = parseFloat(quantita);
                            if (q > 0 && Math.abs((q / qm) - Math.round(q / qm)) > 1e-4) {
                              const snapped = Math.ceil(q / qm) * qm;
                              setQuantita(String(parseFloat(snapped.toFixed(4))));
                            }
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="bop-btn-icon"
                        onClick={() => {
                          const qm = parseFloat(articoloSelezionato?.qtaMinOrdinabile || '0');
                          const step = qm > 0 ? qm : 1;
                          const cur = parseFloat(quantita || '0');
                          const next = cur + step;
                          setQuantita(String(parseFloat(next.toFixed(4))));
                        }}
                        title="Aumenta"
                      >+</button>
                    </div>
                    {(() => {
                      if (!articoloSelezionato) return null;
                      const qm = parseFloat(articoloSelezionato.qtaMinOrdinabile || '0');
                      const q = parseFloat(quantita || '0');
                      if (qm <= 0) {
                        return (
                          <small className="bop-text-muted">
                            Quantità minima ordinabile non valorizzata in anagrafica &mdash; inserimento libero.
                          </small>
                        );
                      }
                      if (q > 0 && Math.abs((q / qm) - Math.round(q / qm)) > 1e-4) {
                        return (
                          <small className="bop-text-danger">
                            La quantità deve essere un multiplo di {formatQuantity(qm)}.
                          </small>
                        );
                      }
                      return (
                        <small className="bop-text-muted">
                          Multipli di {formatQuantity(qm)} {articoloSelezionato.umVendita?.simbolo || ''}.
                        </small>
                      );
                    })()}
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Sconto %</label>
                    <input
                      className="bop-input"
                      type="number"
                      step="0.01"
                      value=""
                      disabled
                      readOnly
                    />
                    <small className="bop-text-muted">
                      Lo sconto si gestisce nel blocco <strong>Calcolo prezzo</strong>.
                    </small>
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Codice IVA</label>
                    <select
                      className="bop-select"
                      value={codiceIvaId ?? ''}
                      onChange={e =>
                        setCodiceIvaId(e.target.value ? parseInt(e.target.value, 10) : null)
                      }
                    >
                      <option value="">— Default testata —</option>
                      {codiciIva.map(iva => (
                        <option key={iva.id} value={iva.id}>
                          {iva.codice} ({formatNumber(parseFloat(iva.aliquota))}%)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Riga 2: due card affiancate */}
                <div className="prev-mod-prezzi-2col">
                  {/* CARD SINISTRA — Calcolo prezzo */}
                  <div className="prev-mod-prezzi-card">
                    <div className="prev-mod-prezzi-card__title">Calcolo prezzo</div>

                    <div className="bop-form-group">
                      <label className="bop-label">Approccio</label>
                      <select
                        className="bop-select"
                        value={approccioCalcolo === 'NESSUNO' ? 'SCONTO_DA_LISTINO' : approccioCalcolo}
                        onChange={e => setApproccioCalcolo(e.target.value as ApproccioCalcoloRiga)}
                      >
                        <option value="SCONTO_DA_LISTINO">Sconto su listino</option>
                        <option value="RICARICO_SU_COSTI">Costi + ricarico %</option>
                      </select>
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">Prezzo listino (€ / {umVendita || 'u.m.'})</label>
                      <input
                        className="bop-input"
                        type="number"
                        step="0.0001"
                        value={prezzoListino}
                        onChange={e => setPrezzoListino(e.target.value)}
                        placeholder="Prezzo u.m. vendita"
                      />
                      <small className="bop-text-muted">
                        Precompilato dall'anagrafica articolo (u.m. vendita). Modificabile.
                      </small>
                    </div>

                    {approccioCalcolo === 'SCONTO_DA_LISTINO' && (
                      <div className="bop-form-group">
                        <label className="bop-label">Sconto su vendite</label>
                        <ScontoInput
                          codice={scontoVenditaCodice}
                          percentuale={scontoVenditaPerc}
                          onChange={(codice, perc) => {
                            setScontoVenditaCodice(codice);
                            setScontoVenditaPerc(perc);
                          }}
                          scontiCatalogo={scontiCatalogo}
                          suggeriti={[
                            scontiFornitore?.vendBasso,
                            scontiFornitore?.vendMedio,
                            scontiFornitore?.vendAlto,
                            scontiFornitore?.vendMax,
                          ].filter(Boolean) as { codice: string; percentualeEquivalente: number }[]}
                          placeholder="es. 10+5+2 oppure 15"
                        />
                      </div>
                    )}

                    {approccioCalcolo === 'RICARICO_SU_COSTI' && (
                      <>
                        <div className="bop-form-group">
                          <label className="bop-label">Sconto su acquisti</label>
                          <ScontoInput
                            codice={scontoAcquistoCodice}
                            percentuale={scontoAcquistoPerc}
                            onChange={(codice, perc) => {
                              setScontoAcquistoCodice(codice);
                              setScontoAcquistoPerc(perc);
                            }}
                            scontiCatalogo={scontiCatalogo}
                            suggeriti={[
                              scontiFornitore?.acqBasso,
                              scontiFornitore?.acqMedio,
                              scontiFornitore?.acqAlto,
                              scontiFornitore?.acqMax,
                            ].filter(Boolean) as { codice: string; percentualeEquivalente: number }[]}
                            placeholder="es. 10+5+2 oppure 15"
                          />
                        </div>
                        <div className="bop-form-group">
                          <label className="bop-label">Ricarico %</label>
                          <input
                            className="bop-input"
                            type="number"
                            step="0.01"
                            value={percentualeRicaricoRiga}
                            onChange={e => setPercentualeRicaricoRiga(e.target.value)}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* CARD DESTRA — Prezzo finale */}
                  <div className="prev-mod-prezzi-card prev-mod-prezzi-card--right">
                    <div className="prev-mod-prezzi-card__title">Prezzo finale</div>

                    <div className="bop-form-group">
                      <label className="bop-label">Prezzo unitario calcolato (€ / {umVendita || 'u.m.'})</label>
                      <input
                        className="bop-input"
                        type="text"
                        value={prezzoCalcolatoLive != null ? formatCurrency(prezzoCalcolatoLive) : '—'}
                        readOnly
                        disabled
                      />
                      <small className="bop-text-muted">
                        Calcolato dal backend. Non editabile.
                      </small>
                    </div>

                    <div className="bop-form-group">
                      <label className="prev-mod-checkbox-label">
                        <input
                          type="checkbox"
                          checked={prezzoOverride}
                          onChange={e => setPrezzoOverride(e.target.checked)}
                        />
                        Sovrascrivi prezzo
                      </label>
                    </div>

                    {prezzoOverride && (
                      <div className="bop-form-group">
                        <label className="bop-label">Prezzo preventivo (€ / {umVendita || 'u.m.'})</label>
                        <input
                          className="bop-input"
                          type="number"
                          step="0.0001"
                          value={prezzoUnitario}
                          onChange={e => setPrezzoUnitario(e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* ── Nodo 1: Riga A_CORPO ──────────────────────────────────── */}
            {tipoRiga === 'A_CORPO' && (
              <>
                <div className="mcr-info">
                  <strong>Riga a corpo.</strong> Il prezzo è manuale e include tutto quanto descritto.
                  I figli (articoli e lavorazioni che compongono il pacchetto) verranno definiti
                  in fase di validazione tecnica dell'ordine, non in preventivo.
                </div>
                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">U.M.</label>
                    <input
                      className="bop-input"
                      value={umVendita}
                      readOnly
                      disabled
                      title="U.M. ereditata dall'anagrafica articolo"
                    />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Quantità <span className="bop-text-danger">*</span></label>
                    <input
                      className="bop-input"
                      type="number"
                      step="0.01"
                      min="0"
                      value={quantita}
                      onChange={e => setQuantita(e.target.value)}
                    />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Prezzo unitario (€ / {umVendita || 'u.m.'}) <span className="bop-text-danger">*</span></label>
                    <input
                      className="bop-input"
                      type="number"
                      step="0.0001"
                      min="0"
                      value={prezzoUnitario}
                      onChange={e => setPrezzoUnitario(e.target.value)}
                    />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Codice IVA</label>
                    <select
                      className="bop-select"
                      value={codiceIvaId ?? ''}
                      onChange={e => setCodiceIvaId(e.target.value ? parseInt(e.target.value, 10) : null)}
                    >
                      <option value="">— Default testata —</option>
                      {codiciIva.map(iva => (
                        <option key={iva.id} value={iva.id}>
                          {iva.codice} ({formatNumber(parseFloat(iva.aliquota))}%)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}

            {/* ── Nodo 1: Riga RICORRENTE ──────────────────────────────── */}
            {tipoRiga === 'RICORRENTE' && articoloId && (
              <div className="mcr-section">
                <div className="mcr-section__title">Dati ricorrenza</div>

                <div className="bop-form-group">
                  <label className="bop-label">Sottotipo</label>
                  {(() => {
                    const tip = articoloSelezionato?.tipologia ?? '';
                    const label = SOTTOTIPO_LABEL[tip as TipologiaRicorrente] ?? tip;
                    return <input type="text" className="bop-input" value={label} readOnly disabled />;
                  })()}
                  <small className="bop-text-muted">Coincide automaticamente con la tipologia dell'articolo.</small>
                </div>

                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">Periodicità di fatturazione <span className="bop-text-danger">*</span></label>
                    <select
                      className="bop-select"
                      value={periodicita}
                      onChange={(e) => setPeriodicita(e.target.value)}
                    >
                      <option value="">— Seleziona —</option>
                      {Object.entries(PERIODICITA_LABEL).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                    <small className="bop-text-muted">Ogni quanto matura ed è emesso il documento ricorrente (mensile, trimestrale, semestrale, annuale).</small>
                  </div>

                  <div className="bop-form-group">
                    <label className="bop-label">Durata totale del contratto (mesi)</label>
                    <input
                      type="number"
                      className="bop-input"
                      min={1}
                      step={1}
                      value={durataMesi}
                      onChange={(e) => setDurataMesi(e.target.value)}
                    />
                    <small className="bop-text-muted">Lunghezza complessiva del contratto. Lascia vuoto per durata indeterminata (perpetua).</small>
                  </div>

                  <div className="bop-form-group">
                    <label className="bop-label">Modalità di determinazione importo <span className="bop-text-danger">*</span></label>
                    <select
                      className="bop-select"
                      value={modalitaPagamento}
                      onChange={(e) => setModalitaPagamento(e.target.value)}
                    >
                      <option value="">— Seleziona —</option>
                      {Object.entries(MODALITA_PAGAMENTO_LABEL).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                    <small className="bop-text-muted">Come si determina l'importo: a preventivo (forfait anticipato), a consuntivo su volumi, a consuntivo su transazioni. Non è la condizione di pagamento della fattura (30/60 gg, RiBa…), che è un campo separato.</small>
                  </div>
                </div>

                {modalitaPagamento === 'A_PREVENTIVO' && (
                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">Quantità <span className="bop-text-danger">*</span></label>
                      <input
                        className="bop-input"
                        type="number"
                        step="0.01"
                        min="0"
                        value={quantita}
                        onChange={e => setQuantita(e.target.value)}
                      />
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Prezzo unitario (€) <span className="bop-text-danger">*</span></label>
                      <input
                        className="bop-input"
                        type="number"
                        step="0.0001"
                        min="0"
                        value={prezzoUnitario}
                        onChange={e => setPrezzoUnitario(e.target.value)}
                      />
                      <small className="bop-text-muted">Importo fisso per ogni emissione di fattura.</small>
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Codice IVA</label>
                      <select
                        className="bop-select"
                        value={codiceIvaId ?? ''}
                        onChange={e => setCodiceIvaId(e.target.value ? parseInt(e.target.value, 10) : null)}
                      >
                        <option value="">— Default testata —</option>
                        {codiciIva.map(iva => (
                          <option key={iva.id} value={iva.id}>
                            {iva.codice} ({formatNumber(parseFloat(iva.aliquota))}%)
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {modalitaPagamento === 'A_CONSUNTIVO_VOLUMI' && (
                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">Tariffa unitaria <span className="bop-text-danger">*</span></label>
                      <input
                        type="number"
                        className="bop-input"
                        step="0.00001"
                        min={0}
                        value={tariffaUnitaria}
                        onChange={(e) => setTariffaUnitaria(e.target.value)}
                      />
                      <small className="bop-text-muted">es. €/sms, €/dispositivo gestito.</small>
                    </div>
                  </div>
                )}

                {modalitaPagamento === 'A_CONSUNTIVO_TRANSAZIONI' && (
                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">Tariffa fissa per transazione <span className="bop-text-danger">*</span></label>
                      <input
                        type="number"
                        className="bop-input"
                        step="0.00001"
                        min={0}
                        value={tariffaFissaTransazione}
                        onChange={(e) => setTariffaFissaTransazione(e.target.value)}
                      />
                      <small className="bop-text-muted">es. 0,03 €/transazione.</small>
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Percentuale sul transato (%) <span className="bop-text-danger">*</span></label>
                      <input
                        type="number"
                        className="bop-input"
                        step="0.0001"
                        min={0}
                        max={100}
                        value={percentualeTransazione}
                        onChange={(e) => setPercentualeTransazione(e.target.value)}
                      />
                      <small className="bop-text-muted">es. 1,2000 (= 1,20%).</small>
                    </div>
                  </div>
                )}

                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">Data inizio</label>
                    <input
                      type="date"
                      className="bop-input"
                      value={dataInizio}
                      onChange={(e) => setDataInizio(e.target.value)}
                    />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Data fine</label>
                    <input
                      type="date"
                      className="bop-input"
                      value={dataFine}
                      onChange={(e) => setDataFine(e.target.value)}
                    />
                    <small className="bop-text-muted">Calcolata da inizio + durata. Modificabile.</small>
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          {/* ── Sezione 5: Componenti ────────────────────────────────────── */}
          {(isBaseCapitolato || (isConfigurato &&
            (modalitaConfig === 'DA_MODELLO' || modalitaConfig === 'DA_PRECONFIGURATO'))) && (
              <div className="mcr-section">
                <div className="mcr-section__title">5. Componenti</div>
                {!currentRigaId ? (
                  <div className="mcr-info">
                    Salva prima la riga per iniziare a configurare i componenti.
                  </div>
                ) : (
                  <>
                    <div className="mcr-componenti-wrap">
                      <table className="bop-table mcr-componenti-table">
                        <thead>
                          <tr className="prev-mod-group-header-row">
                            <th colSpan={2} className="prev-mod-group-header prev-mod-group-header--struct">Struttura</th>
                            <th colSpan={2} className="prev-mod-group-header prev-mod-group-header--qta">Quantità &amp; Sfrido</th>
                            <th colSpan={5} className="prev-mod-group-header prev-mod-group-header--econ">Economia</th>
                            <th></th>
                          </tr>
                          <tr>
                            <th>#</th>
                            <th>Articolo scelto</th>
                            <th>Qtà / UM</th>
                            <th>Sfrido %</th>
                            <th>P. listino €</th>
                            <th>Sc. acq.</th>
                            <th>Sc. ven.</th>
                            <th className="bop-text-right">Costo un.</th>
                            <th className="bop-text-right">P. vendita un.</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {componentiVisibili.length === 0 ? (
                            <tr>
                              <td colSpan={10} className="bop-empty-state">
                                Nessun componente. Usa "Inizializza componenti" o "Aggiungi
                                componente".
                              </td>
                            </tr>
                          ) : (
                            componentiVisibili.map((c, idx) => {
                              const artDett = c.articoloId != null ? articoliDettaglioCache[c.articoloId] : null;
                              const fornitoreId = artDett?.fornitore?.id;
                              const livelliForn = fornitoreId != null ? scontiPerFornitore[fornitoreId] : null;
                              const suggAcq = [
                                livelliForn?.acqBasso,
                                livelliForn?.acqMedio,
                                livelliForn?.acqAlto,
                                livelliForn?.acqMax,
                              ].filter(Boolean) as { codice: string; percentualeEquivalente: number }[];
                              const suggVend = [
                                livelliForn?.vendBasso,
                                livelliForn?.vendMedio,
                                livelliForn?.vendAlto,
                                livelliForn?.vendMax,
                              ].filter(Boolean) as { codice: string; percentualeEquivalente: number }[];
                              const interInfo = componentiInterInfo[c.id];
                              const catMercNome = c.articoloId != null
                                ? articoliDettaglioCache[c.articoloId]?.categoriaMerceologica?.nome
                                : null;
                              const formatoNome = c.articoloId != null
                                ? articoliDettaglioCache[c.articoloId]?.formato?.nome
                                : null;
                              const articoloCaricato = c.articoloId != null && !!articoliDettaglioCache[c.articoloId];
                              // Valori canonici del server: usati come `key` degli input
                              // non controllati, così si risincronizzano quando il
                              // backend cambia il dato (vedi componentiServer).
                              const srv = componentiServer.get(c.id);
                              return (
                                <React.Fragment key={`${c.id}-${c.articoloId ?? 'new'}-${c.codicePrecopy ?? ''}`}>
                                  {/* ── Livello 1: codice, nome, input operativi ── */}
                                  <tr className="mcr-comp-row-a">
                                    <td className="bop-text-muted mcr-comp-idx">{idx + 1}</td>
                                    <td>
                                      <div className="prev-mod-comp-articolo__header">
                                        {c.articoloId != null ? (
                                          <Link
                                            to={`/anagrafiche/articoli/${c.articoloId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="prev-mod-comp-articolo__codice prev-mod-comp-articolo__codice--link"
                                            title="Apri l'anagrafica articolo in una nuova scheda"
                                          >
                                            {c.codicePrecopy || '—'}
                                          </Link>
                                        ) : (
                                          <div className="prev-mod-comp-articolo__codice">
                                            {c.codicePrecopy || '—'}
                                          </div>
                                        )}
                                        <button
                                          type="button"
                                          className="bop-btn-icon bop-btn-icon--sm"
                                          onClick={() => setCambiaArticoloCompId(c.id)}
                                          title="Cambia articolo"
                                        >
                                          <EditIcon />
                                        </button>
                                      </div>
                                      <input
                                        className="bop-input mcr-inp-md"
                                        defaultValue={c.descrizione}
                                        onBlur={e => {
                                          if (e.target.value !== c.descrizione)
                                            aggiornaComponente(c.id, { descrizione: e.target.value });
                                        }}
                                      />
                                    </td>
                                    <td>
                                      <div className="prev-mod-qty-cell">
                                        <input
                                          key={`qta-${srv?.quantita ?? ''}`}
                                          type="number"
                                          step="0.0001"
                                          className="bop-input mcr-inp-xs"
                                          defaultValue={c.quantita ?? ''}
                                          onChange={e => {
                                            const v = e.target.value;
                                            setPatchLive(prev => ({
                                              ...prev,
                                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                              [c.id]: { ...(prev[c.id] || {}), quantita: (v === '' ? null : v) as any },
                                            }));
                                          }}
                                          onBlur={e => {
                                            const v = parseNumOrNull(e.target.value);
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            aggiornaComponenteLive(c.id, { quantita: v as any });
                                          }}
                                        />
                                        {c.umVendita && (
                                          <span className="bop-text-muted prev-mod-qty-cell__um">{c.umVendita}</span>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="mcr-sfrido-cell">
                                        <input
                                          key={`sfrido-${srv?.sfrido ?? ''}`}
                                          type="number"
                                          step="0.01"
                                          className="bop-input mcr-inp-xs"
                                          defaultValue={c.sfrido ?? ''}
                                          onChange={e => {
                                            const v = e.target.value;
                                            setPatchLive(prev => ({
                                              ...prev,
                                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                              [c.id]: { ...(prev[c.id] || {}), sfrido: (v === '' ? null : v) as any },
                                            }));
                                          }}
                                          onBlur={e => {
                                            const v = parseNumOrNull(e.target.value);
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            aggiornaComponenteLive(c.id, { sfrido: v as any });
                                          }}
                                        />
                                        <button
                                          type="button"
                                          className="bop-btn-icon bop-btn-icon--sm"
                                          title="Scegli da tabella sfridi"
                                          onClick={() => setSfridoModalCompId(c.id)}
                                          disabled={!articoliDettaglioCache[c.articoloId ?? -1]?.formato?.id}
                                        >
                                          <SearchIcon />
                                        </button>
                                      </div>
                                      <div className="mcr-cell-footer">
                                        {articoloCaricato && formatoNome && (
                                          <small className="bop-text-muted prev-mod-comp-formato-hint">
                                            {formatoNome}
                                          </small>
                                        )}
                                        <Link
                                          to="/archivi/configurazioni-articoli/sfridi"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="mcr-cell-link"
                                          title="Apri la tabella sfridi in una nuova scheda"
                                        >
                                          <ExternalLinkIcon />
                                        </Link>
                                      </div>
                                    </td>
                                    <td>
                                      <input
                                        key={`plistino-${srv?.prezzoListino ?? ''}`}
                                        type="number"
                                        step="0.01"
                                        className="bop-input mcr-inp-xs"
                                        defaultValue={c.prezzoListino ?? ''}
                                        onChange={e => {
                                          const v = e.target.value;
                                          setPatchLive(prev => ({
                                            ...prev,
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            [c.id]: { ...(prev[c.id] || {}), prezzoListino: (v === '' ? null : v) as any },
                                          }));
                                        }}
                                        onBlur={e => {
                                          const v = parseNumOrNull(e.target.value);
                                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                          aggiornaComponenteLive(c.id, { prezzoListino: v as any });
                                        }}
                                      />
                                      {c.articoloId != null && (
                                        <div className="mcr-cell-footer">
                                          <button
                                            type="button"
                                            className="mcr-cell-link mcr-cell-link--btn"
                                            title="Riallinea il prezzo di listino all'anagrafica articolo"
                                            onClick={() => ricalcolaPrezzoListino(c.id)}
                                            disabled={ricalcoloCompId === c.id}
                                          >
                                            <RefreshIcon />
                                          </button>
                                          <Link
                                            to={`/anagrafiche/articoli/${c.articoloId}/storico-prezzi`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mcr-cell-link"
                                            title="Apri lo storico prezzi di listino in una nuova scheda"
                                          >
                                            <ExternalLinkIcon />
                                          </Link>
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <ScontoInput
                                        codice={c.scontoAcquistoCodice ?? ''}
                                        percentuale={c.scontoAcquisto ?? ''}
                                        onChange={(codice, perc) => {
                                          // patchLive immediata (per formula live)
                                          setPatchLive(prev => ({
                                            ...prev,
                                            [c.id]: {
                                              ...(prev[c.id] || {}),
                                              scontoAcquistoCodice: codice || null,
                                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                              scontoAcquisto: (perc === '' ? null : perc) as any,
                                            },
                                          }));
                                          // e persisti al backend (che al ritorno pulirà la patchLive)
                                          aggiornaComponenteLive(c.id, {
                                            scontoAcquistoCodice: codice || null,
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            scontoAcquisto: (perc === '' ? null : perc) as any,
                                          });
                                        }}
                                        scontiCatalogo={scontiCatalogo}
                                        suggeriti={suggAcq}
                                        placeholder="es. 10+5"
                                      />
                                    </td>
                                    <td>
                                      <ScontoInput
                                        codice={c.scontoVenditaCodice ?? ''}
                                        percentuale={c.scontoVendita ?? ''}
                                        onChange={(codice, perc) => {
                                          // patchLive immediata (per formula live)
                                          setPatchLive(prev => ({
                                            ...prev,
                                            [c.id]: {
                                              ...(prev[c.id] || {}),
                                              scontoVenditaCodice: codice || null,
                                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                              scontoVendita: (perc === '' ? null : perc) as any,
                                            },
                                          }));
                                          // e persisti al backend (che al ritorno pulirà la patchLive)
                                          aggiornaComponenteLive(c.id, {
                                            scontoVenditaCodice: codice || null,
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            scontoVendita: (perc === '' ? null : perc) as any,
                                          });
                                        }}
                                        scontiCatalogo={scontiCatalogo}
                                        suggeriti={suggVend}
                                        placeholder="es. 10+5"
                                      />
                                    </td>
                                    <td className="bop-text-right">
                                      {c.costoUnitario
                                        ? formatCurrency(parseFloat(c.costoUnitario))
                                        : '—'}
                                    </td>
                                    <td className="bop-text-right">
                                      {c.prezzoVenditaUnitario
                                        ? formatCurrency(parseFloat(c.prezzoVenditaUnitario))
                                        : '—'}
                                    </td>
                                    <td rowSpan={2} className="mcr-comp-delete-cell">
                                      <button
                                        className="bop-btn-icon"
                                        onClick={() => eliminaComponente(c.id)}
                                        title="Elimina"
                                      >
                                        <TrashIcon />
                                      </button>
                                    </td>
                                  </tr>
                                  {/* ── Livello 2: metadati (tipologia, formato, intercambiabile) ── */}
                                  <tr className="mcr-comp-row-b">
                                    <td></td>
                                    <td>
                                      {(c.tipologia || catMercNome) && (
                                        <div className="bop-sconto-dropdown-meta">
                                          {c.tipologia && <span>{c.tipologia}</span>}
                                          {c.tipologia && catMercNome && <span> · </span>}
                                          {catMercNome && <span>{catMercNome}</span>}
                                        </div>
                                      )}
                                      {interInfo?.isIntercambiabile && interInfo.numeroAlternativi === 0 && (
                                        <div className="prev-mod-intercambiabile-hint prev-mod-intercambiabile-hint--warning prev-mod-intercambiabile-hint--compact">
                                          Intercambiabile senza alternativi associati.
                                        </div>
                                      )}
                                      {interInfo?.isIntercambiabile && interInfo.numeroAlternativi > 0 && interInfo.articoloMaxPrezzo && (
                                        <div className="prev-mod-intercambiabile-hint prev-mod-intercambiabile-hint--info prev-mod-intercambiabile-hint--compact">
                                          Intercambiabile — {interInfo.numeroAlternativi} alt · max{' '}
                                          <strong>&euro; {formatCurrency(parseFloat(interInfo.prezzoMax || '0'))}</strong>
                                        </div>
                                      )}
                                    </td>
                                    <td colSpan={7}></td>
                                  </tr>
                                </React.Fragment>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                    <button
                      type="button"
                      className="bop-btn bop-btn-secondary"
                      onClick={aggiungiComponente}
                    >
                      + Aggiungi componente
                    </button>
                  </>
                )}
              </div>
            )}

          {/* ── Sezione 6: Calcolo prezzo (nascosta se BASE_CAPITOLATO) ──────── */}
          {isConfigurato && !isBaseCapitolato && (
            <div className="mcr-section">
              <div className="mcr-section__title">6. Calcolo prezzo</div>

              {modalitaConfig === 'DIFFERITA_IN_ORDINE' ? (
                // ── Caso DIFFERITA_IN_ORDINE: prezzo manuale vincolante ──────
                <div className="prev-mod-prezzi-2col">
                  <div className="prev-mod-prezzi-card">
                    <div className="prev-mod-prezzi-card__title">Configurazione differita</div>
                    <div className="mcr-info">
                      La configurazione specifica sarà definita al momento della
                      conversione in ordine. Il prezzo inserito qui è <strong>vincolante
                      verso il cliente</strong>: in sede di ordine, il commerciale
                      costruirà la configurazione rispettando questo prezzo.
                    </div>
                    <div className="mcr-info mcr-info--neutral">
                      Nessun calcolo automatico: non ci sono componenti né listini da sommare.
                    </div>
                  </div>

                  <div className="prev-mod-prezzi-card prev-mod-prezzi-card--right">
                    <div className="prev-mod-prezzi-card__title">Prezzo manuale</div>

                    <div className="bop-form-group">
                      <label className="bop-label">Prezzo unitario (€ / {umVendita || 'u.m.'})</label>
                      <input
                        className="bop-input"
                        type="number"
                        step="0.0001"
                        value={prezzoUnitario}
                        onChange={e => setPrezzoUnitario(e.target.value)}
                        placeholder="0,00"
                      />
                      <small className="bop-text-muted">
                        Inserisci il prezzo concordato con il cliente.
                      </small>
                    </div>

                    {ripartizione && (
                      <div className="mcr-ripartizione">
                        <div className="mcr-ripartizione__title">Ripartizione capitolato</div>
                        <div className="mcr-ripartizione__row">
                          <span>Delta</span>
                          <span>€ {formatCurrency(ripartizione.delta)}</span>
                        </div>
                        <div className="mcr-ripartizione__row">
                          <span>Extra acquirente</span>
                          <span>€ {formatCurrency(ripartizione.extraAcq)}</span>
                        </div>
                        <div className="mcr-ripartizione__row">
                          <span>Quota impresa</span>
                          <span>€ {formatCurrency(ripartizione.impImpr)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // ── Casi DA_MODELLO / DA_PRECONFIGURATO / null: versione con componenti ─
                <div className="prev-mod-prezzi-2col">
                  <div className="prev-mod-prezzi-card">
                    <div className="prev-mod-prezzi-card__title">Calcolo da componenti</div>

                    <div className="bop-form-group">
                      <label className="bop-label">Approccio</label>
                      <div className="mcr-tipo-cap-radio-group">
                        <label className="mcr-radio-pill">
                          <input
                            type="radio"
                            name="approccioCalcoloConf"
                            value="SCONTO_DA_LISTINO"
                            checked={approccioCalcolo !== 'RICARICO_SU_COSTI'}
                            onChange={() => setApproccioCalcolo('SCONTO_DA_LISTINO')}
                          />
                          <span>Prezzo listino con sconto</span>
                        </label>
                        <label className="mcr-radio-pill">
                          <input
                            type="radio"
                            name="approccioCalcoloConf"
                            value="RICARICO_SU_COSTI"
                            checked={approccioCalcolo === 'RICARICO_SU_COSTI'}
                            onChange={() => setApproccioCalcolo('RICARICO_SU_COSTI')}
                          />
                          <span>Costi articoli + ricarico</span>
                        </label>
                      </div>
                      <small className="bop-text-muted">
                        {approccioCalcolo === 'RICARICO_SU_COSTI'
                          ? 'Somma dei costi unitari dei componenti, poi ricarico %'
                          : 'Somma dei prezzi di vendita unitari dei componenti, poi eventuale sconto %'}
                      </small>
                    </div>

                    <div className="bop-form-group">
                      <label className="bop-label">Calcolato da componenti distinta (€)</label>
                      <input
                        className="bop-input"
                        type="text"
                        value={calcolatoDaComponenti != null ? formatCurrency(calcolatoDaComponenti) : '—'}
                        readOnly
                        disabled
                      />
                      <small className="bop-text-muted">
                        {approccioCalcolo === 'RICARICO_SU_COSTI'
                          ? 'Somma di "Costo un." di tutti i componenti'
                          : 'Somma di "P. Vendita un." di tutti i componenti'}
                      </small>
                    </div>

                    {approccioCalcolo !== 'RICARICO_SU_COSTI' && (
                      <div className="bop-form-group">
                        <label className="bop-label">Sconto su prezzo calcolato %</label>
                        <input
                          className="bop-input"
                          type="number"
                          step="0.01"
                          value={scontoSuCalcolato}
                          onChange={e => setScontoSuCalcolato(e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    )}

                    {approccioCalcolo === 'RICARICO_SU_COSTI' && (
                      <div className="bop-form-group">
                        <label className="bop-label">Ricarico riga %</label>
                        <input
                          className="bop-input"
                          type="number"
                          step="0.01"
                          value={percentualeRicaricoRiga}
                          onChange={e => setPercentualeRicaricoRiga(e.target.value)}
                        />
                      </div>
                    )}

                    <div className="bop-form-group">
                      <label className="bop-label">Prezzo calcolato</label>
                      <input
                        className="bop-input"
                        type="text"
                        value={prezzoCalcolatoConfLive != null ? `€ ${formatCurrency(prezzoCalcolatoConfLive)}` : '—'}
                        readOnly
                        disabled
                      />
                      <small className="bop-text-muted">Calcolato live dalla somma dei componenti.</small>
                    </div>

                    <button
                      type="button"
                      className="bop-btn bop-btn-secondary"
                      disabled
                      title="Ricalcolo automatico live — bottone disabilitato"
                    >
                      Ricalcola da componenti
                    </button>
                  </div>

                  <div className="prev-mod-prezzi-card prev-mod-prezzi-card--right">
                    <div className="prev-mod-prezzi-card__title">Prezzo finale</div>

                    <div className="bop-form-group">
                      <label className="bop-label">Prezzo unitario calcolato (€ / {umVendita || 'u.m.'})</label>
                      <input
                        className="bop-input"
                        type="text"
                        value={prezzoCalcolatoConfLive != null ? formatCurrency(prezzoCalcolatoConfLive) : '—'}
                        readOnly
                        disabled
                      />
                      <small className="bop-text-muted">Non editabile. Vista del calcolo corrente.</small>
                    </div>

                    <div className="bop-form-group">
                      <label className="prev-mod-checkbox-label">
                        <input
                          type="checkbox"
                          checked={prezzoOverride}
                          onChange={e => setPrezzoOverride(e.target.checked)}
                        />
                        Sovrascrivi prezzo
                      </label>
                    </div>

                    {prezzoOverride && (
                      <div className="bop-form-group">
                        <label className="bop-label">Prezzo preventivo (€ / {umVendita || 'u.m.'})</label>
                        <input
                          className="bop-input"
                          type="number"
                          step="0.0001"
                          value={prezzoUnitario}
                          onChange={e => setPrezzoUnitario(e.target.value)}
                        />
                      </div>
                    )}

                    {ripartizione && (
                      <div className="mcr-ripartizione">
                        <div className="mcr-ripartizione__title">Ripartizione capitolato</div>
                        <div className="mcr-ripartizione__row">
                          <span>Delta</span>
                          <span>€ {formatCurrency(ripartizione.delta)}</span>
                        </div>
                        <div className="mcr-ripartizione__row">
                          <span>Extra acquirente</span>
                          <span>€ {formatCurrency(ripartizione.extraAcq)}</span>
                        </div>
                        <div className="mcr-ripartizione__row">
                          <span>Quota impresa</span>
                          <span>€ {formatCurrency(ripartizione.impImpr)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Ripartizione capitolato (solo per ARTICOLO non-configurato con link capitolato) */}
          {!isConfigurato && tipoRiga === 'ARTICOLO' && ripartizione && (
            <div className="mcr-section">
              <div className="mcr-ripartizione">
                <div className="mcr-ripartizione__title">Ripartizione capitolato</div>
                <div className="mcr-ripartizione__row">
                  <span>Delta</span>
                  <span>€ {formatCurrency(ripartizione.delta)}</span>
                </div>
                <div className="mcr-ripartizione__row">
                  <span>Extra acquirente</span>
                  <span>€ {formatCurrency(ripartizione.extraAcq)}</span>
                </div>
                <div className="mcr-ripartizione__row">
                  <span>Quota impresa</span>
                  <span>€ {formatCurrency(ripartizione.impImpr)}</span>
                </div>
              </div>
            </div>
          )}

          {rigaEsistente?.raggruppamentoId && rigaEsistente.tipoRiga !== 'DESCRITTIVA' && (
            <div className="prev-modal__section">
              <h4 className="prev-modal__section-title">
                Alternativa
                {altGruppoCorrente && (
                  <span className="prev-badge-gruppo">Gruppo {altGruppoCorrente}</span>
                )}
              </h4>
              <div className="bop-form-group">
                <label className="bop-label">Stato</label>
                <select
                  className="bop-select"
                  value={
                    altTargetId === null ? '' :
                    altTargetId === -2 ? '__resta__' :
                    altTargetId === -1 ? '__nuova__' :
                    String(altTargetId)
                  }
                  onChange={e => {
                    const v = e.target.value;
                    if (v === '') setAltTargetId(null);
                    else if (v === '__resta__') setAltTargetId(-2);
                    else if (v === '__nuova__') setAltTargetId(-1);
                    else setAltTargetId(parseInt(v, 10));
                  }}
                >
                  {altGruppoCorrente && <option value="__resta__">Resta nel Gruppo {altGruppoCorrente}</option>}
                  <option value="">Slega — riga indipendente</option>
                  <option value="__nuova__">Crea nuovo cluster (con prima riga disponibile)</option>
                  {righeDelPreventivo
                    .filter(r =>
                      r.id !== rigaEsistente.id &&
                      r.raggruppamentoId === rigaEsistente.raggruppamentoId &&
                      r.tipoRiga !== 'DESCRITTIVA'
                    )
                    .map(r => {
                      const label = r.alternativaGruppo
                        ? `Aggiungi al Gruppo ${r.alternativaGruppo} — ${r.descrizione}`
                        : `Crea cluster con: ${r.descrizione}`;
                      return <option key={r.id} value={r.id}>{label}</option>;
                    })}
                </select>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Etichetta alternativa <span className="bop-text-muted">(opzionale)</span></label>
                <input
                  className="bop-input"
                  value={altEtichetta}
                  onChange={e => setAltEtichetta(e.target.value)}
                  placeholder="es. economica, premium"
                />
              </div>
            </div>
          )}
        </div>

        <div className="prev-modal__footer">
          <button
            className="bop-btn bop-btn-secondary"
            onClick={handleClose}
            disabled={saving}
          >
            {currentRigaId && isNew && !rigaAppenaCreata ? 'Chiudi' : 'Annulla'}
          </button>
          <button className="bop-btn bop-btn-primary" onClick={salva} disabled={saving}>
            {saving
              ? 'Salvataggio...'
              : currentRigaId
              ? 'Salva modifiche'
              : 'Salva riga'}
          </button>
        </div>
      </div>

      {cambiaArticoloCompId != null && (
        <CambiaArticoloComponentePopover
          compId={cambiaArticoloCompId}
          societaId={preventivo.societaId}
          onClose={() => setCambiaArticoloCompId(null)}
          onSeleziona={async (nuovoArt) => {
            // Per articoli intercambiabili usa il prezzo max tra gli alternativi
            let prezzoListino: number | null = nuovoArt.prezzoListinoVendita != null
              ? parseFloat(nuovoArt.prezzoListinoVendita)
              : null;
            if (nuovoArt.tipologia === 'INTERCAMBIABILE') {
              try {
                // @api GET /api/anagrafiche/articoli/:id/intercambiabili-con-prezzo-max -> {"success":true,"data":{"isIntercambiabile":"boolean","numeroAlternativi":"number","prezzoMax":"string|null","articoloMaxPrezzo":"{id,codice,descrizioneBase}|null"}}
                const r = await apiFetch<{ success: boolean; data: ArticoloIntercambiabileInfo }>(
                  `/api/anagrafiche/articoli/${nuovoArt.id}/intercambiabili-con-prezzo-max`,
                );
                if (r.data?.prezzoMax != null) {
                  prezzoListino = parseFloat(r.data.prezzoMax);
                }
              } catch { /* usa prezzoListinoVendita come fallback */ }
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await aggiornaComponente(cambiaArticoloCompId, {
              articoloId: nuovoArt.id,
              codicePrecopy: nuovoArt.codice,
              descrizione: nuovoArt.descrizioneBase,
              umVendita: nuovoArt.umVendita?.simbolo || null,
              prezzoListino: prezzoListino as any,
              scontoAcquisto: null,
              scontoAcquistoCodice: null,
              scontoVendita: null,
              scontoVenditaCodice: null,
            });
            // Se è il primo componente, aggiorna il campo descrizione della riga
            // anteponendo la descrizione del nuovo articolo separata da " # "
            if (componentiVisibili[0]?.id === cambiaArticoloCompId && nuovoArt.descrizioneBase) {
              setDescrizione(prev => {
                const separatore = prev.indexOf(' # ');
                const codaAttuale = separatore >= 0 ? prev.slice(separatore + 3) : prev;
                return `${nuovoArt.descrizioneBase} # ${codaAttuale}`;
              });
            }
            setCambiaArticoloCompId(null);
          }}
        />
      )}

      {sfridoModalCompId != null && (() => {
        const comp = componenti.find(c => c.id === sfridoModalCompId);
        if (!comp) return null;
        const artDett = comp.articoloId != null ? articoliDettaglioCache[comp.articoloId] : null;
        const formato = artDett?.formato;
        if (!formato?.id) return null;
        const sfridoCorrente = comp.sfrido != null && comp.sfrido !== '' ? parseFloat(String(comp.sfrido)) : null;
        return (
          <ModalSfridoFormato
            societaId={preventivo.societaId}
            formatoId={formato.id}
            formatoNome={formato.nome}
            sfridoCorrente={sfridoCorrente}
            onClose={() => setSfridoModalCompId(null)}
            onSeleziona={async (s) => {
              setPatchLive(prev => ({
                ...prev,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                [comp.id]: { ...(prev[comp.id] || {}), sfrido: s as any },
              }));
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              await aggiornaComponenteLive(comp.id, { sfrido: s as any });
              setSfridoModalCompId(null);
            }}
          />
        );
      })()}
    </div>
  );
}
